/**
 * Block registry.
 *
 * A block id is a small integer. Every block declares:
 *  - per-face atlas tile indices (order: +X, -X, +Y, -Y, +Z, -Z)
 *  - solidity (collides), opacity (blocks light), render pass
 *  - optional light emission and vegetation tint
 *
 * Tile ids are assigned sequentially from the ordered T table below; the
 * atlas layout (see atlasLayout.ts) must hold at least TILE_COUNT tiles.
 */

import { ATLAS_COLS, ATLAS_ROWS } from './atlasLayout';
import { FACE_TILES } from './entityModels';
import { defineRedstoneBlocks, RS_ATTACH, RS_OUTLINES } from './world/redstone/defs';
import { CH_FACING, CH_KIND, CH_OPEN, CH_TYPE, chestId, defineUtilityBlocks, U_CLIMBABLE, U_FACING_FAMILIES, U_OUTLINES } from './game/utility/defs';
import {
  DIR_BITS, stairShape, stateCollision, stateVisual, type ShapeKind, type ShapeState, type StairNb,
} from './shapes';

export enum Pass {
  Opaque = 0,
  Cutout = 1,   // alpha-tested (leaves, glass, plants)
  Translucent = 2, // blended (water)
  Invisible = 3,   // air — never meshed
}

export enum RenderShape {
  Cube = 0,
  Cross = 1, // X-shaped billboard (flowers, grass tufts, torches)
  Flat = 2,  // one horizontal quad low in the cell (petals, lily pads, carpets)
  Model = 3, // vanilla block-model cuboids (doors, chests, beds) — see models.ts
  Fire = 4,  // 4 tilted vertical flame planes (template_fire_floor) — see greedy.ts
  Rail = 5,  // a rail: flat / corner / sloped quad by shape (RAIL_SHAPE) — see greedy.ts, world/rails.ts
}

export interface BlockDef {
  id: number;
  name: string;
  /** Tile index per face: [+X, -X, +Y, -Y, +Z, -Z]. */
  tiles: [number, number, number, number, number, number];
  solid: boolean;
  /** Blocks light fully. */
  opaque: boolean;
  pass: Pass;
  shape: RenderShape;
  /** Light emitted 0..15. */
  light: number;
  /** Extra light attenuation when passing through (e.g. water absorbs 1). */
  filter: number;
  /** Vegetation tint multiplier (grass, leaves). null = untinted. */
  tint: [number, number, number] | null;
  /** Render top face slightly lowered (water surface). */
  surfaceDrop?: number;
  /** Breaks when the block below it is removed (torch, flora, cactus). */
  needsSupport?: boolean;
  /** Offered in the inventory / placeable by the player. */
  placeable?: boolean;
  /** Underwater plant: its cell counts as water (kelp, seagrass). */
  waterlogged?: boolean;
  /** Cross-shaped block that must not sway in the wind (dripstone). */
  rigid?: boolean;
  /** Rendered as a thin torch post instead of a cross. */
  torch?: boolean;
  /** Flat shape: height of the quad above the cell bottom (default 1/16). */
  flatY?: number;
  /** Flat shape: keep the texture axis-aligned (carpets) instead of rotating it per column. */
  flatAligned?: boolean;
  /** What the player collides with, when it is not the whole cell: boxes in
   *  1/16 block units. Omitted means "the whole cell when `solid`, nothing
   *  when not"; a chest is 14x14x14 and a bed's mattress slab 9/16 tall. */
  collision?: readonly CollisionRect[];
}

/** A collision box in 1/16 block units: x0, y0, z0, x1, y1, z1. */
export type CollisionRect = readonly [number, number, number, number, number, number];

const B: BlockDef[] = [];
export const AIR = 0;

export const GRASS_ID = 1;
export const DIRT_ID = 2;
export const STONE_ID = 3;
export const COBBLE_ID = 4;
export const SAND_ID = 5;
export const LOG_ID = 6;
export const LEAVES_ID = 7;
export const PLANKS_ID = 8;
export const GLASS_ID = 9;
export const WATER_ID = 10;
export const SNOW_ID = 11;
export const GRAVEL_ID = 12;
export const BEDROCK_ID = 13;
export const COAL_ID = 14;
export const IRON_ID = 15;
export const GOLD_ID = 16;
export const DIAMOND_ID = 17;
export const GLOWSTONE_ID = 18;
export const TALL_GRASS_ID = 19;
export const POPPY_ID = 20;
export const CACTUS_ID = 21;
export const STONE_BRICK_ID = 22;
export const LAVA_ID = 23;
export const TORCH_ID = 24;
export const PODZOL_ID = 25;
export const MYCELIUM_ID = 26;
export const COARSE_DIRT_ID = 27;
export const ROOTED_DIRT_ID = 28;
export const MOSSY_COBBLE_ID = 29;
export const SMOOTH_STONE_ID = 30;
export const MOSSY_BRICKS_ID = 31;
export const CRACKED_BRICKS_ID = 32;
export const CHISELED_BRICKS_ID = 33;
export const BRICKS_ID = 34;
export const GRANITE_ID = 35;
export const DIORITE_ID = 36;
export const ANDESITE_ID = 37;
export const DEEPSLATE_ID = 38;
export const COBBLED_DEEPSLATE_ID = 39;
export const TUFF_ID = 40;
export const CALCITE_ID = 41;
export const DRIPSTONE_ID = 42;
export const BASALT_ID = 43;
export const SANDSTONE_ID = 44;
export const RED_SANDSTONE_ID = 45;
export const CLAY_ID = 46;
export const TERRACOTTA_ID = 47;
export const OBSIDIAN_ID = 48;
export const CRYING_OBSIDIAN_ID = 49;
export const ICE_ID = 50;
export const PACKED_ICE_ID = 51;
export const BLUE_ICE_ID = 52;
export const MAGMA_ID = 53;
export const SEA_LANTERN_ID = 54;
export const SHROOMLIGHT_ID = 55;
export const NETHERRACK_ID = 56;
export const SOUL_SAND_ID = 57;
export const SOUL_SOIL_ID = 58;
export const NETHER_BRICKS_ID = 59;
export const QUARTZ_ORE_ID = 60;
export const ANCIENT_DEBRIS_ID = 61;
export const COPPER_ORE_ID = 62;
export const REDSTONE_ORE_ID = 63;
export const LAPIS_ORE_ID = 64;
export const EMERALD_ORE_ID = 65;
export const DEEPSLATE_COAL_ORE_ID = 66;
export const DEEPSLATE_IRON_ORE_ID = 67;
export const DEEPSLATE_COPPER_ORE_ID = 68;
export const DEEPSLATE_GOLD_ORE_ID = 69;
export const DEEPSLATE_REDSTONE_ORE_ID = 70;
export const DEEPSLATE_LAPIS_ORE_ID = 71;
export const DEEPSLATE_EMERALD_ORE_ID = 72;
export const DEEPSLATE_DIAMOND_ORE_ID = 73;
export const SPRUCE_LOG_ID = 74;
export const SPRUCE_LEAVES_ID = 75;
export const SPRUCE_PLANKS_ID = 76;
export const BIRCH_LOG_ID = 77;
export const BIRCH_LEAVES_ID = 78;
export const BIRCH_PLANKS_ID = 79;
export const JUNGLE_LOG_ID = 80;
export const JUNGLE_LEAVES_ID = 81;
export const JUNGLE_PLANKS_ID = 82;
export const ACACIA_LOG_ID = 83;
export const ACACIA_LEAVES_ID = 84;
export const ACACIA_PLANKS_ID = 85;
export const DARK_OAK_LOG_ID = 86;
export const DARK_OAK_LEAVES_ID = 87;
export const DARK_OAK_PLANKS_ID = 88;
export const MANGROVE_LOG_ID = 89;
export const MANGROVE_LEAVES_ID = 90;
export const MANGROVE_PLANKS_ID = 91;
export const CHERRY_LOG_ID = 92;
export const CHERRY_LEAVES_ID = 93;
export const CHERRY_PLANKS_ID = 94;
export const PALE_OAK_LOG_ID = 95;
export const PALE_OAK_LEAVES_ID = 96;
export const PALE_OAK_PLANKS_ID = 97;
export const BOOKSHELF_ID = 98;
export const CRAFTING_TABLE_ID = 99;
/** Furnace front (the door with the hole) on the +Z face — the id worldgen and
 *  existing saves already use, and vanilla's own default facing. The rotated
 *  variants are FURNACE_FACING_1..3, defined with the chests further down. */
export const FURNACE_FACING_0 = 100;
export const FURNACE_ID = FURNACE_FACING_0;
export const TNT_ID = 101;
export const PUMPKIN_ID = 102;
export const MELON_ID = 103;
export const HAY_BALE_ID = 104;
export const SOUL_TORCH_ID = 105;
export const DANDELION_ID = 106;
export const BLUE_ORCHID_ID = 107;
export const ALLIUM_ID = 108;
export const AZURE_BLUET_ID = 109;
export const RED_TULIP_ID = 110;
export const ORANGE_TULIP_ID = 111;
export const WHITE_TULIP_ID = 112;
export const PINK_TULIP_ID = 113;
export const OXEYE_DAISY_ID = 114;
export const CORNFLOWER_ID = 115;
export const LILY_OF_THE_VALLEY_ID = 116;
export const DEAD_BUSH_ID = 117;
export const RED_MUSHROOM_ID = 118;
export const BROWN_MUSHROOM_ID = 119;
export const OAK_SAPLING_ID = 120;
export const FERN_ID = 121;

// --- atlas tiles (assigned sequentially) -------------------------------------
let tileN = 0;
const t = (): number => tileN++;

export const T = {
  // terrain
  GRASS_TOP: t(), GRASS_SIDE: t(), DIRT: t(), STONE: t(), COBBLE: t(), SAND: t(),
  LOG_SIDE: t(), LOG_TOP: t(), LEAVES: t(), PLANKS: t(), GLASS: t(), WATER: t(),
  SNOW: t(), SNOW_SIDE: t(), GRAVEL: t(), BEDROCK: t(), COAL: t(), IRON: t(),
  GOLD: t(), DIAMOND: t(), GLOWSTONE: t(), TALL_GRASS: t(), POPPY: t(),
  CACTUS_SIDE: t(), CACTUS_TOP: t(), STONE_BRICK: t(), LAVA: t(), TORCH: t(),
  PODZOL_TOP: t(), PODZOL_SIDE: t(), MYCELIUM_TOP: t(), MYCELIUM_SIDE: t(),
  COARSE_DIRT: t(), ROOTED_DIRT: t(), MOSSY_COBBLE: t(), SMOOTH_STONE: t(),
  MOSSY_BRICKS: t(), CRACKED_BRICKS: t(), CHISELED_BRICKS: t(), BRICKS: t(),
  GRANITE: t(), DIORITE: t(), ANDESITE: t(),
  DEEPSLATE: t(), DEEPSLATE_TOP: t(), COBBLED_DEEPSLATE: t(), TUFF: t(),
  CALCITE: t(), DRIPSTONE: t(), BASALT_TOP: t(), BASALT_SIDE: t(),
  SANDSTONE_TOP: t(), SANDSTONE: t(), SANDSTONE_BOTTOM: t(),
  RED_SANDSTONE_TOP: t(), RED_SANDSTONE: t(), RED_SANDSTONE_BOTTOM: t(),
  CLAY: t(), TERRACOTTA: t(), OBSIDIAN: t(), CRYING_OBSIDIAN: t(),
  ICE: t(), PACKED_ICE: t(), BLUE_ICE: t(), MAGMA: t(),
  SEA_LANTERN: t(), SHROOMLIGHT: t(),
  NETHERRACK: t(), SOUL_SAND: t(), SOUL_SOIL: t(), NETHER_BRICKS: t(),
  QUARTZ_ORE: t(), ANCIENT_DEBRIS_SIDE: t(), ANCIENT_DEBRIS_TOP: t(),
  COPPER_ORE: t(), REDSTONE_ORE: t(), LAPIS_ORE: t(), EMERALD_ORE: t(),
  DEEPSLATE_COAL_ORE: t(), DEEPSLATE_IRON_ORE: t(), DEEPSLATE_COPPER_ORE: t(),
  DEEPSLATE_GOLD_ORE: t(), DEEPSLATE_REDSTONE_ORE: t(), DEEPSLATE_LAPIS_ORE: t(),
  DEEPSLATE_EMERALD_ORE: t(), DEEPSLATE_DIAMOND_ORE: t(),
  // wood species
  SPRUCE_PLANKS: t(), SPRUCE_LOG: t(), SPRUCE_LOG_TOP: t(), SPRUCE_LEAVES: t(),
  BIRCH_PLANKS: t(), BIRCH_LOG: t(), BIRCH_LOG_TOP: t(), BIRCH_LEAVES: t(),
  JUNGLE_PLANKS: t(), JUNGLE_LOG: t(), JUNGLE_LOG_TOP: t(), JUNGLE_LEAVES: t(),
  ACACIA_PLANKS: t(), ACACIA_LOG: t(), ACACIA_LOG_TOP: t(), ACACIA_LEAVES: t(),
  DARK_OAK_PLANKS: t(), DARK_OAK_LOG: t(), DARK_OAK_LOG_TOP: t(), DARK_OAK_LEAVES: t(),
  MANGROVE_PLANKS: t(), MANGROVE_LOG: t(), MANGROVE_LOG_TOP: t(), MANGROVE_LEAVES: t(),
  CHERRY_PLANKS: t(), CHERRY_LOG: t(), CHERRY_LOG_TOP: t(), CHERRY_LEAVES: t(),
  PALE_OAK_PLANKS: t(), PALE_OAK_LOG: t(), PALE_OAK_LOG_TOP: t(), PALE_OAK_LEAVES: t(),
  // misc
  BOOKSHELF: t(), CRAFTING_TABLE_TOP: t(), CRAFTING_TABLE_SIDE: t(),
  FURNACE_TOP: t(), FURNACE_SIDE: t(),
  TNT_TOP: t(), TNT_SIDE: t(), TNT_BOTTOM: t(),
  PUMPKIN_TOP: t(), PUMPKIN_SIDE: t(), MELON_TOP: t(), MELON_SIDE: t(),
  HAY_TOP: t(), HAY_SIDE: t(), SOUL_TORCH: t(),
  // flora
  DANDELION: t(), BLUE_ORCHID: t(), ALLIUM: t(), AZURE_BLUET: t(),
  TULIP_RED: t(), TULIP_ORANGE: t(), TULIP_WHITE: t(), TULIP_PINK: t(),
  OXEYE_DAISY: t(), CORNFLOWER: t(), LILY_OF_THE_VALLEY: t(), DEAD_BUSH: t(),
  MUSHROOM_RED: t(), MUSHROOM_BROWN: t(), OAK_SAPLING: t(), FERN: t(),
  // stone / deepslate variants
  POLISHED_GRANITE: t(), POLISHED_DIORITE: t(), POLISHED_ANDESITE: t(),
  POLISHED_DEEPSLATE: t(), DEEPSLATE_BRICKS: t(), DEEPSLATE_TILES: t(),
  CHISELED_DEEPSLATE: t(),
  // sandstone variants
  SMOOTH_SANDSTONE: t(), CUT_SANDSTONE: t(), CHISELED_SANDSTONE: t(),
  SMOOTH_RED_SANDSTONE: t(), CUT_RED_SANDSTONE: t(), CHISELED_RED_SANDSTONE: t(),
  // soils & organics
  RED_SAND: t(), MUD: t(), PACKED_MUD: t(), MUD_BRICKS: t(), MOSS: t(),
  // metals & minerals
  COPPER_BLOCK: t(), CUT_COPPER: t(), EXPOSED_COPPER: t(), WEATHERED_COPPER: t(),
  OXIDIZED_COPPER: t(), RAW_IRON: t(), RAW_COPPER: t(), RAW_GOLD: t(),
  IRON_BLOCK: t(), GOLD_BLOCK: t(), DIAMOND_BLOCK: t(), EMERALD_BLOCK: t(),
  LAPIS_BLOCK: t(), REDSTONE_BLOCK: t(), COAL_BLOCK: t(), AMETHYST: t(),
  // ocean & misc
  PRISMARINE: t(), PRISMARINE_BRICKS: t(), DARK_PRISMARINE: t(),
  BONE_SIDE: t(), BONE_TOP: t(), DRIED_KELP: t(), SPONGE: t(),
  QUARTZ_SIDE: t(), QUARTZ_TOP: t(), JACK_O_LANTERN: t(),
  // flora
  SUNFLOWER: t(), ROSE_BUSH: t(), LILAC: t(), PEONY: t(),
  AZALEA_LEAVES: t(), FLOWERING_AZALEA_LEAVES: t(), AZALEA: t(), FLOWERING_AZALEA: t(),
  // worldgen features
  KELP: t(), KELP_PLANT: t(), SEAGRASS: t(), SUGAR_CANE: t(), BAMBOO: t(), SWEET_BERRY_BUSH: t(),
  RED_MUSHROOM_BLOCK: t(), BROWN_MUSHROOM_BLOCK: t(), MUSHROOM_STEM: t(),
  SCULK: t(), CAVE_VINES: t(), DRIPSTONE_UP: t(), DRIPSTONE_DOWN: t(),
  SMOOTH_BASALT: t(), POWDER_SNOW: t(),
  CORAL_TUBE: t(), CORAL_BRAIN: t(), CORAL_BUBBLE: t(), CORAL_FIRE: t(), CORAL_HORN: t(),
  // ground cover (flat quads)
  PINK_PETALS: t(), LEAF_LITTER: t(), LILY_PAD: t(), WILDFLOWERS: t(),
  // structures
  COBWEB: t(), RAIL: t(),
  DIRT_PATH_TOP: t(), DIRT_PATH_SIDE: t(), FARMLAND: t(), WHEAT: t(),
  OAK_DOOR_BOTTOM: t(), OAK_DOOR_TOP: t(), SPRUCE_DOOR_BOTTOM: t(), SPRUCE_DOOR_TOP: t(),
  // fire (flint and steel placement)
  FIRE_0: t(), FIRE_1: t(),
  // Furnace door (the hole), on the face the furnace points at.
  FURNACE_FRONT: t(),
  // nether dimension
  BLACKSTONE: t(), BLACKSTONE_TOP: t(),
  CRIMSON_NYLIUM: t(), CRIMSON_NYLIUM_SIDE: t(), WARPED_NYLIUM: t(), WARPED_NYLIUM_SIDE: t(),
  CRIMSON_STEM: t(), CRIMSON_STEM_TOP: t(), WARPED_STEM: t(), WARPED_STEM_TOP: t(),
  NETHER_WART_BLOCK: t(), WARPED_WART_BLOCK: t(), NETHER_PORTAL: t(), NETHER_GOLD_ORE: t(),
  // enchanting (combat agent)
  ENCHANTING_TABLE_TOP: t(), ENCHANTING_TABLE_SIDE: t(), ENCHANTING_TABLE_BOTTOM: t(), ANVIL: t(), ANVIL_TOP: t(),
  CHIPPED_ANVIL_TOP: t(), DAMAGED_ANVIL_TOP: t(),
  SMITHING_TABLE_TOP: t(), SMITHING_TABLE_SIDE: t(), SMITHING_TABLE_FRONT: t(), SMITHING_TABLE_BOTTOM: t(),
  // nether, round 2
  NETHER_WART_0: t(), NETHER_WART_1: t(), NETHER_WART_2: t(),
  CRIMSON_FUNGUS: t(), WARPED_FUNGUS: t(), CRIMSON_ROOTS: t(), WARPED_ROOTS: t(),
  WEEPING_VINES: t(), WEEPING_VINES_PLANT: t(), TWISTING_VINES: t(), TWISTING_VINES_PLANT: t(),
  SOUL_FIRE: t(), SPAWNER: t(), CHISELED_NETHER_BRICKS: t(), CRACKED_NETHER_BRICKS: t(),
  // farming (world/crops.ts): growth-stage textures
  WHEAT_S0: t(), WHEAT_S1: t(), WHEAT_S2: t(), WHEAT_S3: t(), WHEAT_S4: t(), WHEAT_S5: t(), WHEAT_S6: t(),
  FARMLAND_DRY: t(),
  CARROTS_S0: t(), CARROTS_S1: t(), CARROTS_S2: t(), CARROTS_S3: t(), POTATOES_S0: t(), POTATOES_S1: t(), POTATOES_S2: t(), POTATOES_S3: t(), BEETROOTS_S0: t(), BEETROOTS_S1: t(), BEETROOTS_S2: t(), BEETROOTS_S3: t(),
  // village workstations (entities/village)
  COMPOSTER_TOP: t(), COMPOSTER_SIDE: t(), COMPOSTER_BOTTOM: t(),
  LECTERN_TOP: t(), LECTERN_SIDES: t(), LECTERN_FRONT: t(), LECTERN_BASE: t(),
  BARREL_TOP: t(), BARREL_SIDE: t(), BARREL_BOTTOM: t(),
  FLETCHING_TABLE_TOP: t(), FLETCHING_TABLE_SIDE: t(), FLETCHING_TABLE_FRONT: t(),
  GRINDSTONE_SIDE: t(), GRINDSTONE_ROUND: t(), GRINDSTONE_PIVOT: t(),
  SMOKER_TOP: t(), SMOKER_SIDE: t(), SMOKER_FRONT: t(), SMOKER_BOTTOM: t(),
  BLAST_FURNACE_TOP: t(), BLAST_FURNACE_SIDE: t(), BLAST_FURNACE_FRONT: t(),
  // lit furnace fronts (painted from the unlit art in textures.ts: glowing opening)
  FURNACE_FRONT_ON: t(), SMOKER_FRONT_ON: t(), BLAST_FURNACE_FRONT_ON: t(),
  STONECUTTER_TOP: t(), STONECUTTER_SIDE: t(), STONECUTTER_BOTTOM: t(),
  BREWING_STAND: t(),
  // the End (worldgen/end.ts, game/endPortal.ts)
  END_STONE: t(), END_STONE_BRICKS: t(), END_FRAME_TOP: t(), END_FRAME_SIDE: t(), END_FRAME_EYE: t(),
  END_PORTAL: t(), DRAGON_EGG: t(),
  // structures round 2 (worldgen/structures: bastion, trial chamber, end city)
  IRON_BARS: t(), POLISHED_BLACKSTONE_BRICKS: t(), GILDED_BLACKSTONE: t(), TRIAL_SPAWNER: t(), VAULT: t(),
  CHORUS_PLANT: t(), CHORUS_FLOWER: t(), PURPUR: t(), PURPUR_PILLAR: t(),
  // real vanilla trapdoor textures, in WOODS order
  TRAPDOOR_0: t(), TRAPDOOR_1: t(), TRAPDOOR_2: t(), TRAPDOOR_3: t(), TRAPDOOR_4: t(), TRAPDOOR_5: t(), TRAPDOOR_6: t(), TRAPDOOR_7: t(), TRAPDOOR_8: t(),
  // wither skeleton skull (entities/boss/wither.ts summons the wither from three on a soul sand T)
  WITHER_SKULL_FACE: t(), WITHER_SKULL_SIDE: t(), WITHER_SKULL_TOP: t(),
  // sculk shrieker / catalyst (entities/boss/shrieker.ts, warden.ts); procedural until the vanilla art is extracted
  SHRIEKER_TOP: t(), SHRIEKER_SIDE: t(), SHRIEKER_BOTTOM: t(), SHRIEKER_SUMMON_TOP: t(), CATALYST_TOP: t(), CATALYST_SIDE: t(), CATALYST_BOTTOM: t(),
  // minecart rails (world/rails.ts): curved and powered (always powered, there is no redstone) art
  RAIL_CORNER: t(), POWERED_RAIL_ON: t(),
  // redstone (world/redstone/defs.ts)
  RS_DUST_DOT: t(), RS_DUST_LINE: t(), RS_TORCH: t(), RS_TORCH_OFF: t(), LEVER: t(), REPEATER: t(), REPEATER_ON: t(), COMPARATOR: t(), COMPARATOR_ON: t(),
  RS_LAMP: t(), RS_LAMP_ON: t(), OBSERVER_FRONT: t(), OBSERVER_BACK: t(), OBSERVER_BACK_ON: t(), OBSERVER_SIDE: t(), OBSERVER_TOP: t(),
  PISTON_TOP: t(), PISTON_TOP_STICKY: t(), PISTON_SIDE: t(), PISTON_BOTTOM: t(), PISTON_INNER: t(), NOTE_BLOCK: t(), TARGET_TOP: t(), TARGET_SIDE: t(),
  DAYLIGHT_TOP: t(), DAYLIGHT_INV_TOP: t(), DAYLIGHT_SIDE: t(), TRIPWIRE_HOOK: t(), TRIPWIRE: t(), IRON_DOOR_BOTTOM: t(), IRON_DOOR_TOP: t(), IRON_TRAPDOOR: t(),
  POWERED_RAIL: t(), DETECTOR_RAIL: t(), DETECTOR_RAIL_ON: t(), ACTIVATOR_RAIL: t(), ACTIVATOR_RAIL_ON: t(),
  // containers and utility blocks (game/utility/defs.ts); procedural until the vanilla art is extracted
  HOPPER_OUTSIDE: t(), HOPPER_TOP: t(), HOPPER_INSIDE: t(), DISPENSER_FRONT: t(), DISPENSER_FRONT_V: t(), DROPPER_FRONT: t(), DROPPER_FRONT_V: t(),
  SHULKER_TOP: t(), SHULKER_SIDE: t(), SHULKER_BOTTOM: t(), CAULDRON_SIDE: t(), CAULDRON_TOP: t(), CAULDRON_INNER: t(), CAULDRON_BOTTOM: t(),
  CAKE_TOP: t(), CAKE_SIDE: t(), CAKE_INNER: t(), CAKE_BOTTOM: t(), LADDER: t(), VINE: t(), CHAIN: t(), LANTERN: t(), SOUL_LANTERN: t(),
  CAMPFIRE_LOG: t(), CAMPFIRE_LOG_LIT: t(), CAMPFIRE_FIRE: t(), FLOWER_POT: t(), ITEM_FRAME: t(), GLOW_ITEM_FRAME: t(), LIGHTNING_ROD: t(),
  CANDLE: t(), CANDLE_LIT: t(), COMPOSTER_COMPOST: t(), COMPOSTER_READY: t(),
};
/** Trapdoor tile per WOODS index. */
export const TRAPDOOR_TILES: number[] = [T.TRAPDOOR_0, T.TRAPDOOR_1, T.TRAPDOOR_2, T.TRAPDOOR_3, T.TRAPDOOR_4, T.TRAPDOOR_5, T.TRAPDOOR_6, T.TRAPDOOR_7, T.TRAPDOOR_8];

// 16 vanilla dye colors, reused for terracotta / concrete / wool.
export const COLOR_KEYS = [
  'white', 'orange', 'magenta', 'light_blue', 'yellow', 'lime', 'pink',
  'gray', 'light_gray', 'cyan', 'purple', 'blue', 'brown', 'green', 'red', 'black',
] as const;

/** colorKey -> tile id, e.g. COLOR_TILES.orange_terracotta. */
export const COLOR_TILES: Record<string, number> = {};
for (const c of COLOR_KEYS) {
  COLOR_TILES[c + '_terracotta'] = t();
  COLOR_TILES[c + '_concrete'] = t();
  COLOR_TILES[c + '_wool'] = t();
}

/** colorKey -> block id for each color set (worldgen uses terracotta). */
export const TERRACOTTA_IDS: Record<string, number> = {};
export const CONCRETE_IDS: Record<string, number> = {};
export const WOOL_IDS: Record<string, number> = {};

/** Chest and bed faces (entityModels.ts FACE_TILES): one tile per distinct face
 *  rect of the vanilla entity sheets, ids ENTITY_TILE_BASE + i. */
export const ENTITY_TILE_BASE = tileN;
tileN += FACE_TILES.length;

/** Total number of atlas tiles — must fit ATLAS_COLS * ATLAS_ROWS. */
export const TILE_COUNT = tileN;
if (TILE_COUNT > ATLAS_COLS * ATLAS_ROWS) {
  throw new Error(`atlas overflow: ${TILE_COUNT} tiles > ${ATLAS_COLS * ATLAS_ROWS} slots (atlasLayout.ts)`);
}

type Six = [number, number, number, number, number, number];
/** Same tile on all six faces. */
const all = (tile: number): Six => [tile, tile, tile, tile, tile, tile];
/** top / side / bottom. */
const tsb = (top: number, side: number, bottom: number): Six => [side, side, top, bottom, side, side];

/** The four vertical faces in facing order: 0 = +Z, 1 = +X, 2 = -Z, 3 = -X.
 *  A facing is the cell-space face a directional block's front points out of. */
const FRONT_FACE: readonly number[] = [4, 0, 5, 1];
/** A cube with `front` on the face `facing` points out of, `side` on the other
 *  three vertical faces, and `top` above and below (vanilla's furnace bottom is
 *  its top texture).  Face order is [+X, -X, +Y, -Y, +Z, -Z] — see BlockDef.tiles. */
const frontFaced = (front: number, side: number, top: number, facing: 0 | 1 | 2 | 3): Six => {
  const tiles: Six = [side, side, top, top, side, side];
  tiles[FRONT_FACE[facing]] = front;
  return tiles;
};

const FLORA = {
  solid: false, opaque: false, pass: Pass.Cutout, shape: RenderShape.Cross, needsSupport: true,
} as const;

function def(
  id: number,
  name: string,
  tiles: Six,
  opts: Partial<BlockDef> = {}
): void {
  B[id] = {
    id,
    name,
    tiles,
    solid: true,
    opaque: true,
    pass: Pass.Opaque,
    shape: opts.shape ?? RenderShape.Cube,
    light: 0,
    filter: 0,
    tint: null,
    placeable: true,
    ...opts,
  };
}

// --- world blocks ------------------------------------------------------------

def(0, 'air', all(0), { solid: false, opaque: false, pass: Pass.Invisible, placeable: false });

def(1, 'Grass Block', tsb(T.GRASS_TOP, T.GRASS_SIDE, T.DIRT), { tint: [0.55, 0.85, 0.42] });
def(2, 'Dirt', all(T.DIRT));
def(3, 'Stone', all(T.STONE));
def(4, 'Cobblestone', all(T.COBBLE));
def(5, 'Sand', all(T.SAND));
def(6, 'Oak Log', tsb(T.LOG_TOP, T.LOG_SIDE, T.LOG_TOP));
def(7, 'Oak Leaves', all(T.LEAVES), {
  opaque: false, pass: Pass.Cutout, filter: 1, tint: [0.45, 0.8, 0.35],
});
def(8, 'Oak Planks', all(T.PLANKS));
def(9, 'Glass', all(T.GLASS), { opaque: false, pass: Pass.Cutout });
def(10, 'Water', all(T.WATER), {
  solid: false, opaque: false, pass: Pass.Translucent, filter: 1, surfaceDrop: 1 / 9, placeable: false,
});
def(11, 'Snowy Grass Block', tsb(T.SNOW, T.SNOW_SIDE, T.DIRT));
def(12, 'Gravel', all(T.GRAVEL));
def(13, 'Bedrock', all(T.BEDROCK));
def(14, 'Coal Ore', all(T.COAL));
def(15, 'Iron Ore', all(T.IRON));
def(16, 'Gold Ore', all(T.GOLD));
def(17, 'Diamond Ore', all(T.DIAMOND));
def(18, 'Glowstone', all(T.GLOWSTONE), { light: 15 });
def(19, 'Short Grass', all(T.TALL_GRASS), { ...FLORA, tint: [0.55, 0.85, 0.42] });
def(20, 'Poppy', all(T.POPPY), FLORA);
def(21, 'Cactus', tsb(T.CACTUS_TOP, T.CACTUS_SIDE, T.CACTUS_TOP), { needsSupport: true });
def(22, 'Stone Bricks', all(T.STONE_BRICK));
def(23, 'Lava', all(T.LAVA), {
  solid: false, light: 15, opaque: false, pass: Pass.Translucent, surfaceDrop: 1 / 9, placeable: false,
});
def(24, 'Torch', all(T.TORCH), { ...FLORA, light: 14, torch: true });

def(25, 'Podzol', tsb(T.PODZOL_TOP, T.PODZOL_SIDE, T.DIRT));
def(26, 'Mycelium', tsb(T.MYCELIUM_TOP, T.MYCELIUM_SIDE, T.DIRT), { tint: [0.72, 0.60, 0.72] });
def(27, 'Coarse Dirt', all(T.COARSE_DIRT));
def(28, 'Rooted Dirt', all(T.ROOTED_DIRT));
def(29, 'Mossy Cobblestone', all(T.MOSSY_COBBLE));
def(30, 'Smooth Stone', all(T.SMOOTH_STONE));
def(31, 'Mossy Stone Bricks', all(T.MOSSY_BRICKS));
def(32, 'Cracked Stone Bricks', all(T.CRACKED_BRICKS));
def(33, 'Chiseled Stone Bricks', all(T.CHISELED_BRICKS));
def(34, 'Bricks', all(T.BRICKS));
def(35, 'Granite', all(T.GRANITE));
def(36, 'Diorite', all(T.DIORITE));
def(37, 'Andesite', all(T.ANDESITE));
def(38, 'Deepslate', tsb(T.DEEPSLATE_TOP, T.DEEPSLATE, T.DEEPSLATE_TOP));
def(39, 'Cobbled Deepslate', all(T.COBBLED_DEEPSLATE));
def(40, 'Tuff', all(T.TUFF));
def(41, 'Calcite', all(T.CALCITE));
def(42, 'Dripstone Block', all(T.DRIPSTONE));
def(43, 'Basalt', tsb(T.BASALT_TOP, T.BASALT_SIDE, T.BASALT_TOP));
def(44, 'Sandstone', tsb(T.SANDSTONE_TOP, T.SANDSTONE, T.SANDSTONE_BOTTOM));
def(45, 'Red Sandstone', tsb(T.RED_SANDSTONE_TOP, T.RED_SANDSTONE, T.RED_SANDSTONE_BOTTOM));
def(46, 'Clay', all(T.CLAY));
def(47, 'Terracotta', all(T.TERRACOTTA));
def(48, 'Obsidian', all(T.OBSIDIAN));
def(49, 'Crying Obsidian', all(T.CRYING_OBSIDIAN), { light: 10 });
def(50, 'Ice', all(T.ICE), { opaque: false, pass: Pass.Cutout });
def(51, 'Packed Ice', all(T.PACKED_ICE));
def(52, 'Blue Ice', all(T.BLUE_ICE));
def(53, 'Magma Block', all(T.MAGMA), { light: 3 });
def(54, 'Sea Lantern', all(T.SEA_LANTERN), { light: 15 });
def(55, 'Shroomlight', all(T.SHROOMLIGHT), { light: 15 });
def(56, 'Netherrack', all(T.NETHERRACK));
def(57, 'Soul Sand', all(T.SOUL_SAND));
def(58, 'Soul Soil', all(T.SOUL_SOIL));
def(59, 'Nether Bricks', all(T.NETHER_BRICKS));
def(60, 'Nether Quartz Ore', all(T.QUARTZ_ORE));
def(61, 'Ancient Debris', tsb(T.ANCIENT_DEBRIS_TOP, T.ANCIENT_DEBRIS_SIDE, T.ANCIENT_DEBRIS_TOP));
def(62, 'Copper Ore', all(T.COPPER_ORE));
def(63, 'Redstone Ore', all(T.REDSTONE_ORE));
def(64, 'Lapis Ore', all(T.LAPIS_ORE));
def(65, 'Emerald Ore', all(T.EMERALD_ORE));
def(66, 'Deepslate Coal Ore', all(T.DEEPSLATE_COAL_ORE));
def(67, 'Deepslate Iron Ore', all(T.DEEPSLATE_IRON_ORE));
def(68, 'Deepslate Copper Ore', all(T.DEEPSLATE_COPPER_ORE));
def(69, 'Deepslate Gold Ore', all(T.DEEPSLATE_GOLD_ORE));
def(70, 'Deepslate Redstone Ore', all(T.DEEPSLATE_REDSTONE_ORE));
def(71, 'Deepslate Lapis Ore', all(T.DEEPSLATE_LAPIS_ORE));
def(72, 'Deepslate Emerald Ore', all(T.DEEPSLATE_EMERALD_ORE));
def(73, 'Deepslate Diamond Ore', all(T.DEEPSLATE_DIAMOND_ORE));

// --- wood species (log, leaves, planks) ---------------------------------------

def(74, 'Spruce Log', tsb(T.SPRUCE_LOG_TOP, T.SPRUCE_LOG, T.SPRUCE_LOG_TOP));
def(75, 'Spruce Leaves', all(T.SPRUCE_LEAVES), { opaque: false, pass: Pass.Cutout, filter: 1, tint: [0.38, 0.60, 0.38] });
def(76, 'Spruce Planks', all(T.SPRUCE_PLANKS));
def(77, 'Birch Log', tsb(T.BIRCH_LOG_TOP, T.BIRCH_LOG, T.BIRCH_LOG_TOP));
def(78, 'Birch Leaves', all(T.BIRCH_LEAVES), { opaque: false, pass: Pass.Cutout, filter: 1, tint: [0.50, 0.65, 0.33] });
def(79, 'Birch Planks', all(T.BIRCH_PLANKS));
def(80, 'Jungle Log', tsb(T.JUNGLE_LOG_TOP, T.JUNGLE_LOG, T.JUNGLE_LOG_TOP));
def(81, 'Jungle Leaves', all(T.JUNGLE_LEAVES), { opaque: false, pass: Pass.Cutout, filter: 1, tint: [0.35, 0.79, 0.24] });
def(82, 'Jungle Planks', all(T.JUNGLE_PLANKS));
def(83, 'Acacia Log', tsb(T.ACACIA_LOG_TOP, T.ACACIA_LOG, T.ACACIA_LOG_TOP));
def(84, 'Acacia Leaves', all(T.ACACIA_LEAVES), { opaque: false, pass: Pass.Cutout, filter: 1, tint: [0.50, 0.75, 0.30] });
def(85, 'Acacia Planks', all(T.ACACIA_PLANKS));
def(86, 'Dark Oak Log', tsb(T.DARK_OAK_LOG_TOP, T.DARK_OAK_LOG, T.DARK_OAK_LOG_TOP));
def(87, 'Dark Oak Leaves', all(T.DARK_OAK_LEAVES), { opaque: false, pass: Pass.Cutout, filter: 1, tint: [0.45, 0.80, 0.35] });
def(88, 'Dark Oak Planks', all(T.DARK_OAK_PLANKS));
def(89, 'Mangrove Log', tsb(T.MANGROVE_LOG_TOP, T.MANGROVE_LOG, T.MANGROVE_LOG_TOP));
def(90, 'Mangrove Leaves', all(T.MANGROVE_LEAVES), { opaque: false, pass: Pass.Cutout, filter: 1, tint: [0.40, 0.75, 0.30] });
def(91, 'Mangrove Planks', all(T.MANGROVE_PLANKS));
def(92, 'Cherry Log', tsb(T.CHERRY_LOG_TOP, T.CHERRY_LOG, T.CHERRY_LOG_TOP));
def(93, 'Cherry Leaves', all(T.CHERRY_LEAVES), { opaque: false, pass: Pass.Cutout, filter: 1 });
def(94, 'Cherry Planks', all(T.CHERRY_PLANKS));
def(95, 'Pale Oak Log', tsb(T.PALE_OAK_LOG_TOP, T.PALE_OAK_LOG, T.PALE_OAK_LOG_TOP));
def(96, 'Pale Oak Leaves', all(T.PALE_OAK_LEAVES), { opaque: false, pass: Pass.Cutout, filter: 1, tint: [0.71, 0.79, 0.66] });
def(97, 'Pale Oak Planks', all(T.PALE_OAK_PLANKS));

// --- utility / decorative ------------------------------------------------------

def(98, 'Bookshelf', tsb(T.PLANKS, T.BOOKSHELF, T.PLANKS));
def(99, 'Crafting Table', tsb(T.CRAFTING_TABLE_TOP, T.CRAFTING_TABLE_SIDE, T.PLANKS));
// Furnace: see the facing family in the structure-block section below — its
// front carries the door, so it can't be a single tile on every side.
def(101, 'TNT', tsb(T.TNT_TOP, T.TNT_SIDE, T.TNT_BOTTOM));
def(102, 'Pumpkin', tsb(T.PUMPKIN_TOP, T.PUMPKIN_SIDE, T.PUMPKIN_TOP));
def(103, 'Melon', tsb(T.MELON_TOP, T.MELON_SIDE, T.MELON_TOP));
def(104, 'Hay Bale', tsb(T.HAY_TOP, T.HAY_SIDE, T.HAY_TOP));
def(105, 'Soul Torch', all(T.SOUL_TORCH), { ...FLORA, light: 10, torch: true });

// --- flora (cross-shaped, need support) ----------------------------------------

def(106, 'Dandelion', all(T.DANDELION), FLORA);
def(107, 'Blue Orchid', all(T.BLUE_ORCHID), FLORA);
def(108, 'Allium', all(T.ALLIUM), FLORA);
def(109, 'Azure Bluet', all(T.AZURE_BLUET), FLORA);
def(110, 'Red Tulip', all(T.TULIP_RED), FLORA);
def(111, 'Orange Tulip', all(T.TULIP_ORANGE), FLORA);
def(112, 'White Tulip', all(T.TULIP_WHITE), FLORA);
def(113, 'Pink Tulip', all(T.TULIP_PINK), FLORA);
def(114, 'Oxeye Daisy', all(T.OXEYE_DAISY), FLORA);
def(115, 'Cornflower', all(T.CORNFLOWER), FLORA);
def(116, 'Lily of the Valley', all(T.LILY_OF_THE_VALLEY), FLORA);
def(117, 'Dead Bush', all(T.DEAD_BUSH), FLORA);
def(118, 'Red Mushroom', all(T.MUSHROOM_RED), FLORA);
def(119, 'Brown Mushroom', all(T.MUSHROOM_BROWN), FLORA);
def(120, 'Oak Sapling', all(T.OAK_SAPLING), FLORA);
def(121, 'Fern', all(T.FERN), { ...FLORA, tint: [0.55, 0.85, 0.42] });

// --- appended blocks (ids assigned sequentially) -------------------------------
// Named ids that worldgen or other systems reference:
export const RED_SAND_ID = 122;

let nextBlock = 123;
const bid = (): number => nextBlock++;

// stone / deepslate variants
def(bid(), 'Polished Granite', all(T.POLISHED_GRANITE));
def(bid(), 'Polished Diorite', all(T.POLISHED_DIORITE));
def(bid(), 'Polished Andesite', all(T.POLISHED_ANDESITE));
def(bid(), 'Polished Deepslate', all(T.POLISHED_DEEPSLATE));
def(bid(), 'Deepslate Bricks', all(T.DEEPSLATE_BRICKS));
def(bid(), 'Deepslate Tiles', all(T.DEEPSLATE_TILES));
def(bid(), 'Chiseled Deepslate', all(T.CHISELED_DEEPSLATE));

// red sand + sandstone variants
def(RED_SAND_ID, 'Red Sand', all(T.RED_SAND));
def(bid(), 'Smooth Sandstone', all(T.SMOOTH_SANDSTONE));
def(bid(), 'Cut Sandstone', tsb(T.SANDSTONE_TOP, T.CUT_SANDSTONE, T.SANDSTONE_TOP));
def(bid(), 'Chiseled Sandstone', tsb(T.SANDSTONE_TOP, T.CHISELED_SANDSTONE, T.SANDSTONE_TOP));
def(bid(), 'Smooth Red Sandstone', all(T.SMOOTH_RED_SANDSTONE));
def(bid(), 'Cut Red Sandstone', tsb(T.RED_SANDSTONE_TOP, T.CUT_RED_SANDSTONE, T.RED_SANDSTONE_TOP));
def(bid(), 'Chiseled Red Sandstone', tsb(T.RED_SANDSTONE_TOP, T.CHISELED_RED_SANDSTONE, T.RED_SANDSTONE_TOP));

// mud & organics
export const MUD_ID = bid();
def(MUD_ID, 'Mud', all(T.MUD));
def(bid(), 'Packed Mud', all(T.PACKED_MUD));
def(bid(), 'Mud Bricks', all(T.MUD_BRICKS));
export const MOSS_ID = bid();
def(MOSS_ID, 'Moss Block', all(T.MOSS));

// copper family
def(bid(), 'Block of Copper', all(T.COPPER_BLOCK));
def(bid(), 'Cut Copper', all(T.CUT_COPPER));
def(bid(), 'Exposed Copper', all(T.EXPOSED_COPPER));
def(bid(), 'Weathered Copper', all(T.WEATHERED_COPPER));
def(bid(), 'Oxidized Copper', all(T.OXIDIZED_COPPER));

// raw ore blocks
export const RAW_IRON_BLOCK_ID = bid();
def(RAW_IRON_BLOCK_ID, 'Block of Raw Iron', all(T.RAW_IRON));
export const RAW_COPPER_BLOCK_ID = bid();
def(RAW_COPPER_BLOCK_ID, 'Block of Raw Copper', all(T.RAW_COPPER));
def(bid(), 'Block of Raw Gold', all(T.RAW_GOLD));

// mineral blocks
def(bid(), 'Block of Iron', all(T.IRON_BLOCK));
export const GOLD_BLOCK_ID = bid();
def(GOLD_BLOCK_ID, 'Block of Gold', all(T.GOLD_BLOCK));
def(bid(), 'Block of Diamond', all(T.DIAMOND_BLOCK));
def(bid(), 'Block of Emerald', all(T.EMERALD_BLOCK));
def(bid(), 'Block of Lapis', all(T.LAPIS_BLOCK));
def(bid(), 'Block of Redstone', all(T.REDSTONE_BLOCK));
def(bid(), 'Block of Coal', all(T.COAL_BLOCK));
export const AMETHYST_ID = bid();
def(AMETHYST_ID, 'Block of Amethyst', all(T.AMETHYST));

// ocean & misc
def(bid(), 'Prismarine', all(T.PRISMARINE));
def(bid(), 'Prismarine Bricks', all(T.PRISMARINE_BRICKS));
def(bid(), 'Dark Prismarine', all(T.DARK_PRISMARINE));
def(bid(), 'Bone Block', tsb(T.BONE_TOP, T.BONE_SIDE, T.BONE_TOP));
def(bid(), 'Dried Kelp Block', all(T.DRIED_KELP));
def(bid(), 'Sponge', all(T.SPONGE));
def(bid(), 'Quartz Block', tsb(T.QUARTZ_TOP, T.QUARTZ_SIDE, T.QUARTZ_TOP));
def(bid(), "Jack o'Lantern", tsb(T.PUMPKIN_TOP, T.JACK_O_LANTERN, T.PUMPKIN_TOP), { light: 15 });

// large flora (rendered as cross)
export const SUNFLOWER_ID = bid();
def(SUNFLOWER_ID, 'Sunflower', all(T.SUNFLOWER), FLORA);
export const ROSE_BUSH_ID = bid();
def(ROSE_BUSH_ID, 'Rose Bush', all(T.ROSE_BUSH), FLORA);
export const LILAC_ID = bid();
def(LILAC_ID, 'Lilac', all(T.LILAC), FLORA);
export const PEONY_ID = bid();
def(PEONY_ID, 'Peony', all(T.PEONY), FLORA);

// azalea
export const AZALEA_LEAVES_ID = bid();
def(AZALEA_LEAVES_ID, 'Azalea Leaves', all(T.AZALEA_LEAVES), { opaque: false, pass: Pass.Cutout, filter: 1 });
export const FLOWERING_AZALEA_LEAVES_ID = bid();
def(FLOWERING_AZALEA_LEAVES_ID, 'Flowering Azalea Leaves', all(T.FLOWERING_AZALEA_LEAVES), { opaque: false, pass: Pass.Cutout, filter: 1 });
export const AZALEA_ID = bid();
def(AZALEA_ID, 'Azalea', all(T.AZALEA), FLORA);
export const FLOWERING_AZALEA_ID = bid();
def(FLOWERING_AZALEA_ID, 'Flowering Azalea', all(T.FLOWERING_AZALEA), FLORA);

// color sets: terracotta, concrete, wool
for (const c of COLOR_KEYS) {
  const label = c.replace('_', ' ').replace(/\b\w/g, (m) => m.toUpperCase());
  const terr = bid();
  TERRACOTTA_IDS[c] = terr;
  def(terr, `${label} Terracotta`, all(COLOR_TILES[c + '_terracotta']));
  const conc = bid();
  CONCRETE_IDS[c] = conc;
  def(conc, `${label} Concrete`, all(COLOR_TILES[c + '_concrete']));
  const wool = bid();
  WOOL_IDS[c] = wool;
  def(wool, `${label} Wool`, all(COLOR_TILES[c + '_wool']));
}

// --- worldgen feature blocks (appended so earlier ids stay stable) --------------

const UNDERWATER = { ...FLORA, needsSupport: false, waterlogged: true, filter: 1 } as const;
export const KELP_ID = bid();
def(KELP_ID, 'Kelp', all(T.KELP), UNDERWATER);
export const SEAGRASS_ID = bid();
def(SEAGRASS_ID, 'Seagrass', all(T.SEAGRASS), UNDERWATER);
export const SUGAR_CANE_ID = bid();
def(SUGAR_CANE_ID, 'Sugar Cane', all(T.SUGAR_CANE), { ...FLORA, tint: [0.55, 0.85, 0.42] });
export const BAMBOO_ID = bid();
def(BAMBOO_ID, 'Bamboo', all(T.BAMBOO), FLORA);
export const SWEET_BERRY_BUSH_ID = bid();
def(SWEET_BERRY_BUSH_ID, 'Sweet Berry Bush', all(T.SWEET_BERRY_BUSH), FLORA);
export const RED_MUSHROOM_BLOCK_ID = bid();
def(RED_MUSHROOM_BLOCK_ID, 'Red Mushroom Block', all(T.RED_MUSHROOM_BLOCK));
export const BROWN_MUSHROOM_BLOCK_ID = bid();
def(BROWN_MUSHROOM_BLOCK_ID, 'Brown Mushroom Block', all(T.BROWN_MUSHROOM_BLOCK));
export const MUSHROOM_STEM_ID = bid();
def(MUSHROOM_STEM_ID, 'Mushroom Stem', all(T.MUSHROOM_STEM));
export const SCULK_ID = bid();
def(SCULK_ID, 'Sculk', all(T.SCULK));
export const CAVE_VINES_ID = bid();
def(CAVE_VINES_ID, 'Cave Vines', all(T.CAVE_VINES), { ...FLORA, needsSupport: false, light: 14 });
export const POINTED_DRIPSTONE_UP_ID = bid();
def(POINTED_DRIPSTONE_UP_ID, 'Pointed Dripstone', all(T.DRIPSTONE_UP), { ...FLORA, rigid: true });
export const POINTED_DRIPSTONE_DOWN_ID = bid();
def(POINTED_DRIPSTONE_DOWN_ID, 'Hanging Pointed Dripstone', all(T.DRIPSTONE_DOWN), {
  ...FLORA, needsSupport: false, rigid: true,
});
export const SMOOTH_BASALT_ID = bid();
def(SMOOTH_BASALT_ID, 'Smooth Basalt', all(T.SMOOTH_BASALT));
export const POWDER_SNOW_ID = bid();
def(POWDER_SNOW_ID, 'Powder Snow', all(T.POWDER_SNOW));
export const SNOW_BLOCK_ID = bid();
def(SNOW_BLOCK_ID, 'Snow Block', all(T.SNOW));
/** Fallen logs: [species][axis 0 = X, 1 = Z]; bark all round, rings on the two ends. */
export const FALLEN_LOG_IDS: number[][] = [];
for (const [label, top, side] of [
  ['Oak', T.LOG_TOP, T.LOG_SIDE], ['Birch', T.BIRCH_LOG_TOP, T.BIRCH_LOG], ['Spruce', T.SPRUCE_LOG_TOP, T.SPRUCE_LOG],
] as const) {
  const xId = bid();
  def(xId, `Fallen ${label} Log`, [top, top, side, side, side, side], { placeable: false });
  const zId = bid();
  def(zId, `Fallen ${label} Log`, [side, side, side, side, top, top], { placeable: false });
  FALLEN_LOG_IDS.push([xId, zId]);
}
/** Warm-ocean reef blocks: tube, brain, bubble, fire, horn. */
export const CORAL_IDS: number[] = [];
for (const [label, tile] of [
  ['Tube', T.CORAL_TUBE], ['Brain', T.CORAL_BRAIN], ['Bubble', T.CORAL_BUBBLE],
  ['Fire', T.CORAL_FIRE], ['Horn', T.CORAL_HORN],
] as const) {
  const id = bid();
  CORAL_IDS.push(id);
  def(id, `${label} Coral Block`, all(tile));
}

// ground cover: a flat quad lying on the block below (no collision)
const GROUND_COVER = {
  solid: false, opaque: false, pass: Pass.Cutout, shape: RenderShape.Flat, needsSupport: true,
} as const;
export const PINK_PETALS_ID = bid();
def(PINK_PETALS_ID, 'Pink Petals', all(T.PINK_PETALS), GROUND_COVER);
export const LEAF_LITTER_ID = bid();
def(LEAF_LITTER_ID, 'Leaf Litter', all(T.LEAF_LITTER), GROUND_COVER);
/** Floats on water: occupies the air cell directly above a water surface. */
export const LILY_PAD_ID = bid();
def(LILY_PAD_ID, 'Lily Pad', all(T.LILY_PAD), {
  ...GROUND_COVER, needsSupport: false, flatY: 0.02, tint: [0.125, 0.5, 0.19], // vanilla 0x208030
});
export const MOSS_CARPET_ID = bid();
def(MOSS_CARPET_ID, 'Moss Carpet', all(T.MOSS), { ...GROUND_COVER, flatAligned: true });
export const SNOW_LAYER_ID = bid();
def(SNOW_LAYER_ID, 'Snow', all(T.SNOW), { ...GROUND_COVER, flatAligned: true });
export const WILDFLOWERS_ID = bid();
def(WILDFLOWERS_ID, 'Wildflowers', all(T.WILDFLOWERS), GROUND_COVER);

// flowing water: one block per fluid level, like vanilla's `level` block state.
// WATER_ID is the source (amount 8); these are never placed by hand.
const FLOWING_WATER = {
  solid: false, opaque: false, pass: Pass.Translucent, filter: 1, placeable: false,
} as const;
/** Flowing water by amount 1..7 (index 0 unused); height = amount / 9. */
export const FLOWING_WATER_IDS: number[] = [AIR];
for (let amount = 1; amount <= 7; amount++) {
  const id = bid();
  FLOWING_WATER_IDS.push(id);
  def(id, 'Flowing Water', all(T.WATER), FLOWING_WATER);
}
/** Water fed from the block above (a waterfall column): full amount, not a source. */
export const FALLING_WATER_ID = bid();
def(FALLING_WATER_ID, 'Falling Water', all(T.WATER), FLOWING_WATER);

// --- structure blocks ----------------------------------------------------------

/** Chest ids encode the player-facing direction (cell-space face the latch
 *  points out of): 0 = +Z, 1 = +X, 2 = -Z, 3 = -X.  Closed and open chests
 *  share the same facing, so right-click toggling keeps the orientation.
 *  Only CHEST_FACING_0 is placeable from the hotbar — the placement code
 *  swaps to the right facing based on the player's yaw. */
/** Chests collide as their 14x14x14 box, not as a whole cell (vanilla
 *  ChestBlock.getShape), so the player can stand on the 14/16 lid. */
const CHEST_COLLISION: readonly CollisionRect[] = [[1, 0, 1, 15, 14, 15]];
export const CHEST_FACING_0 = bid();
def(CHEST_FACING_0, 'Chest', all(T.PLANKS), {
  solid: true, opaque: false, pass: Pass.Cutout, shape: RenderShape.Model, collision: CHEST_COLLISION,
});
export const CHEST_FACING_1 = bid();
def(CHEST_FACING_1, 'Chest', all(T.PLANKS), {
  solid: true, opaque: false, pass: Pass.Cutout, shape: RenderShape.Model, placeable: false, collision: CHEST_COLLISION,
});
export const CHEST_FACING_2 = bid();
def(CHEST_FACING_2, 'Chest', all(T.PLANKS), {
  solid: true, opaque: false, pass: Pass.Cutout, shape: RenderShape.Model, placeable: false, collision: CHEST_COLLISION,
});
export const CHEST_FACING_3 = bid();
def(CHEST_FACING_3, 'Chest', all(T.PLANKS), {
  solid: true, opaque: false, pass: Pass.Cutout, shape: RenderShape.Model, placeable: false, collision: CHEST_COLLISION,
});
/** Lid swung open (right-click toggles). Same facing as the closed chest. */
export const CHEST_OPEN_FACING_0 = bid();
def(CHEST_OPEN_FACING_0, 'Chest', all(T.PLANKS), {
  solid: true, opaque: false, pass: Pass.Cutout, shape: RenderShape.Model, placeable: false, collision: CHEST_COLLISION,
});
export const CHEST_OPEN_FACING_1 = bid();
def(CHEST_OPEN_FACING_1, 'Chest', all(T.PLANKS), {
  solid: true, opaque: false, pass: Pass.Cutout, shape: RenderShape.Model, placeable: false, collision: CHEST_COLLISION,
});
export const CHEST_OPEN_FACING_2 = bid();
def(CHEST_OPEN_FACING_2, 'Chest', all(T.PLANKS), {
  solid: true, opaque: false, pass: Pass.Cutout, shape: RenderShape.Model, placeable: false, collision: CHEST_COLLISION,
});
export const CHEST_OPEN_FACING_3 = bid();
def(CHEST_OPEN_FACING_3, 'Chest', all(T.PLANKS), {
  solid: true, opaque: false, pass: Pass.Cutout, shape: RenderShape.Model, placeable: false, collision: CHEST_COLLISION,
});
/** Back-compat aliases: existing code (structures, worldgen) refers to the
 *  "vanilla" default facing = +Z.  These names are what people grep for. */
export const CHEST_ID = CHEST_FACING_0;
export const CHEST_OPEN_ID = CHEST_OPEN_FACING_0;
/** Furnace ids, same scheme as the chests: the front (door + hole) is on the
 *  face the facing points out of.  FURNACE_FACING_0 is FURNACE_ID, the variant
 *  the hotbar carries and worldgen places. */
def(FURNACE_FACING_0, 'Furnace', frontFaced(T.FURNACE_FRONT, T.FURNACE_SIDE, T.FURNACE_TOP, 0));
export const FURNACE_FACING_1 = bid();
def(FURNACE_FACING_1, 'Furnace',
  frontFaced(T.FURNACE_FRONT, T.FURNACE_SIDE, T.FURNACE_TOP, 1), { placeable: false });
export const FURNACE_FACING_2 = bid();
def(FURNACE_FACING_2, 'Furnace',
  frontFaced(T.FURNACE_FRONT, T.FURNACE_SIDE, T.FURNACE_TOP, 2), { placeable: false });
export const FURNACE_FACING_3 = bid();
def(FURNACE_FACING_3, 'Furnace',
  frontFaced(T.FURNACE_FRONT, T.FURNACE_SIDE, T.FURNACE_TOP, 3), { placeable: false });
export const COBWEB_ID = bid();
def(COBWEB_ID, 'Cobweb', all(T.COBWEB), { ...FLORA, needsSupport: false, rigid: true });
export const RAIL_ID = bid();
def(RAIL_ID, 'Rail', all(T.RAIL), { ...GROUND_COVER, flatAligned: true });
export const DIRT_PATH_ID = bid();
def(DIRT_PATH_ID, 'Dirt Path', tsb(T.DIRT_PATH_TOP, T.DIRT_PATH_SIDE, T.DIRT));
export const FARMLAND_ID = bid();
/** Farmland: vanilla's 15/16-tall block (models.ts draws it), moist variant; FARMLAND_DRY_ID is the dry one. */
const FARMLAND_OPTS = { opaque: false, shape: RenderShape.Model, collision: [[0, 0, 0, 16, 15, 16]] as readonly CollisionRect[] };
def(FARMLAND_ID, 'Farmland', tsb(T.FARMLAND, T.DIRT, T.DIRT), FARMLAND_OPTS);
export const WHEAT_ID = bid();
def(WHEAT_ID, 'Wheat', all(T.WHEAT), FLORA);

const BED_MODEL = {
  solid: false, opaque: false, pass: Pass.Cutout, shape: RenderShape.Model,
  needsSupport: true, rigid: true, placeable: false,
  // Vanilla BedBlock's shape: the mattress slab, 3/16..9/16 over the whole
  // footprint, with a 3x3x3 leg at each outer corner. The legs only fill the
  // strip under the slab (0..3), which nothing can reach — the slab already
  // blocks that cell from the side and from above — so the slab alone is the
  // collision shape. 9/16 is under the 0.6 step height, so beds are steps.
  collision: [[0, 3, 0, 16, 9, 16]],
} as const;

// --- doors ---------------------------------------------------------------------
// Vanilla DoorBlock keeps facing, hinge, open and half in block state; here each
// combination is its own id (like the chest facings). `facing` is vanilla's
// FACING — the way the placer looked — in the chest order (0 = +Z, 1 = +X,
// 2 = -Z, 3 = -X); `hinge` is the side the hinge is on as the placer saw it
// (0 = left, 1 = right). Shape and swing come straight from DoorBlock.getShape.

export interface DoorState {
  kind: number;
  facing: 0 | 1 | 2 | 3;
  hinge: 0 | 1;
  open: boolean;
  top: boolean;
}
export const DOOR_KINDS = [
  { name: 'Oak Door', bottom: T.OAK_DOOR_BOTTOM, top: T.OAK_DOOR_TOP },
  { name: 'Spruce Door', bottom: T.SPRUCE_DOOR_BOTTOM, top: T.SPRUCE_DOOR_TOP },
] as const;
/** Door state per block id (undefined for non-doors). */
export const DOOR_STATE: (DoorState | undefined)[] = [];
const DOOR_IDS: number[][] = DOOR_KINDS.map(() => []);
const doorKey = (f: number, h: number, open: boolean, top: boolean): number =>
  ((f * 2 + h) * 2 + (open ? 1 : 0)) * 2 + (top ? 1 : 0);

// DoorBlock's four AABBs, each named for the facing whose closed door it is
const DOOR_S: CollisionRect = [0, 0, 0, 16, 16, 3];
const DOOR_N: CollisionRect = [0, 0, 13, 16, 16, 16];
const DOOR_W: CollisionRect = [13, 0, 0, 16, 16, 16];
const DOOR_E: CollisionRect = [0, 0, 0, 3, 16, 16];
/** DoorBlock.getShape: [facing] -> [closed, open with left hinge, open with right hinge]. */
const DOOR_SHAPES: readonly (readonly [CollisionRect, CollisionRect, CollisionRect])[] = [
  [DOOR_S, DOOR_W, DOOR_E], // +Z
  [DOOR_E, DOOR_S, DOOR_N], // +X
  [DOOR_N, DOOR_E, DOOR_W], // -Z
  [DOOR_W, DOOR_N, DOOR_S], // -X
];
/** The panel a door state occupies, in 1/16 units. */
export function doorShape(s: DoorState): CollisionRect {
  return DOOR_SHAPES[s.facing][s.open ? 1 + s.hinge : 0];
}

function defDoor(kind: number, facing: 0 | 1 | 2 | 3, hinge: 0 | 1, open: boolean, top: boolean): number {
  const id = bid();
  const k = DOOR_KINDS[kind];
  const state: DoorState = { kind, facing, hinge, open, top };
  def(id, k.name, all(top ? k.top : k.bottom), {
    solid: !open, opaque: false, pass: Pass.Cutout, shape: RenderShape.Model,
    needsSupport: true, rigid: true, collision: [doorShape(state)],
    // the closed lower half is the inventory entry; placement picks the state
    placeable: facing === 0 && hinge === 0 && !open && !top,
  });
  DOOR_STATE[id] = state;
  DOOR_IDS[kind][doorKey(facing, hinge, open, top)] = id;
  return id;
}

/** The block id of a door kind in a given state. */
export function doorId(kind: number, facing: number, hinge: number, open: boolean, top: boolean): number {
  return DOOR_IDS[kind][doorKey(facing, hinge, open, top)];
}

// The first state of each kind keeps the ids doors had before they carried
// state (facing +Z, left hinge); the rest are appended at the end of the
// registry (defRemainingDoorStates) so no other block id moves.
export const OAK_DOOR_BOTTOM_ID = defDoor(0, 0, 0, false, false);
export const OAK_DOOR_TOP_ID = defDoor(0, 0, 0, false, true);
export const OAK_DOOR_OPEN_BOTTOM_ID = defDoor(0, 0, 0, true, false);
export const OAK_DOOR_OPEN_TOP_ID = defDoor(0, 0, 0, true, true);
export const SPRUCE_DOOR_BOTTOM_ID = defDoor(1, 0, 0, false, false);
export const SPRUCE_DOOR_TOP_ID = defDoor(1, 0, 0, false, true);
export const SPRUCE_DOOR_OPEN_BOTTOM_ID = defDoor(1, 0, 0, true, false);
export const SPRUCE_DOOR_OPEN_TOP_ID = defDoor(1, 0, 0, true, true);

// Bed: two blocks (head + foot), each rendered with vanilla's BedRenderer
// model (entityModels.ts) — a 16x6x16 mattress at 3..9 px with legs at the
// outer corners. Walk-over, and right-click sleeps until dawn.  Foot is what the player
// places from the hotbar — the head half is generated alongside, oriented by
// the player's yaw.
export const BED_FOOT_ID = bid();
def(BED_FOOT_ID, 'Red Bed', all(T.PLANKS), { ...BED_MODEL, placeable: true });
export const BED_HEAD_ID = bid();
def(BED_HEAD_ID, 'Red Bed', all(T.PLANKS), BED_MODEL);

/** Fire: a non-solid, light-emitting block of flame planes. Vanilla renders it
 *  as 4 crossed vertical quads (template_fire_floor), tilted 22.5deg outward
 *  and 22.4/16 blocks tall — NOT a flat quad on the ground. Placeable only via
 *  flint and steel. */
export const FIRE_ID = bid();
def(FIRE_ID, 'Fire', all(T.FIRE_0), {
  solid: false, opaque: false, pass: Pass.Cutout, shape: RenderShape.Fire,
  needsSupport: true, light: 15, placeable: false,
});

// --- torches: one id per wall they can hang on --------------------------------
// Vanilla keeps a torch's mount in block state; here each wall gets its own id
// (like the chest facings above) so placement, the support rule and the mesher
// all agree on which way the shaft leans. The ids above (TORCH_ID, SOUL_TORCH_ID)
// stay the floor-standing torch, which is what worldgen and the hotbar use.
/** Wall ids per standing torch id: `TORCH_WALLS.get(base)![face]`. */
export const TORCH_WALLS: Map<number, number[]> = new Map();

/** Every wall id, with the face it sticks to (used to build the lookup tables). */
export const WALL_TORCHES: Array<[number, number]> = [];

/**
 * Non-torch blocks that hang on one neighbour (wall signs, item frames, ladders,
 * vines, hanging lanterns): [id, the face of the neighbour that holds it].
 * Kept apart from the torch tables so none of them is drawn or boxed as a torch.
 */
export const ATTACHED: Array<[number, number]> = [];
/** Attached ids per inventory id: `ATTACH_VARIANTS.get(base)![face]` (what `placeIdFor` picks for the clicked face). */
export const ATTACH_VARIANTS: Map<number, number[]> = new Map();

function defWallTorches(baseId: number, name: string, tile: number, light: number): void {
  const byFace: number[] = [];
  for (const face of [0, 1, 4, 5]) { // +X, -X, +Z, -Z; floors and ceilings have no id
    const id = bid();
    def(id, name, all(tile), { ...FLORA, light, torch: true, placeable: false });
    byFace[face] = id;
    WALL_TORCHES.push([id, face]);
  }
  TORCH_WALLS.set(baseId, byFace);
}
defWallTorches(TORCH_ID, 'Torch', T.TORCH, 14);
defWallTorches(SOUL_TORCH_ID, 'Soul Torch', T.SOUL_TORCH, 10);

/** Right-click toggles these ids (doors, chest lids). */
export const TOGGLE: Record<number, number> = {
  [CHEST_FACING_0]: CHEST_OPEN_FACING_0, [CHEST_OPEN_FACING_0]: CHEST_FACING_0,
  [CHEST_FACING_1]: CHEST_OPEN_FACING_1, [CHEST_OPEN_FACING_1]: CHEST_FACING_1,
  [CHEST_FACING_2]: CHEST_OPEN_FACING_2, [CHEST_OPEN_FACING_2]: CHEST_FACING_2,
  [CHEST_FACING_3]: CHEST_OPEN_FACING_3, [CHEST_OPEN_FACING_3]: CHEST_FACING_3,
};
/** True for any open-chest id (lid raised). Chest state (plain / trapped, single / double half) is in game/utility/defs.ts. */
export function isChestOpen(id: number): boolean {
  return CH_KIND[id] >= 0 && CH_OPEN[id] === 1;
}
/** Open-chest id matching the given closed-chest id. */
export function chestOpenOf(closedId: number): number | undefined {
  if (CH_KIND[closedId] < 0 || CH_OPEN[closedId] === 1) return undefined;
  return chestId(CH_KIND[closedId], CH_TYPE[closedId], CH_FACING[closedId], true);
}
/** Closed-chest id matching the given open-chest id. */
export function chestClosedOf(openId: number): number | undefined {
  if (CH_KIND[openId] < 0 || CH_OPEN[openId] === 0) return undefined;
  return chestId(CH_KIND[openId], CH_TYPE[openId], CH_FACING[openId], false);
}
/** Facing direction (0..3) encoded in any chest id (plain or trapped, single or half). -1 if not a chest. */
export function chestFacing(id: number): -1 | 0 | 1 | 2 | 3 {
  return CH_FACING[id] as -1 | 0 | 1 | 2 | 3;
}

/** The directional-cube families, indexed by facing: [0 = +Z, 1 = +X, 2 = -Z,
 *  3 = -X].  Placement carries the +Z variant and swaps in the rotation the
 *  player's yaw asks for. */
export const FURNACE_FACINGS: readonly [number, number, number, number] =
  [FURNACE_FACING_0, FURNACE_FACING_1, FURNACE_FACING_2, FURNACE_FACING_3];
export const CHEST_FACINGS: readonly [number, number, number, number] =
  [CHEST_FACING_0, CHEST_FACING_1, CHEST_FACING_2, CHEST_FACING_3];
export const FACING_FAMILIES: Array<readonly [number, number, number, number]> =
  [FURNACE_FACINGS, CHEST_FACINGS];
/** Anvil families join FACING_FAMILIES once they are defined (bottom of the file). */

/** The four facing variants of `id`'s block, or null if it isn't directional. */
export function facingFamilyOf(id: number): readonly [number, number, number, number] | null {
  for (const family of FACING_FAMILIES) if (family.indexOf(id) >= 0) return family;
  return null;
}
// The remaining door states (see the door section), after every other block.
for (let kind = 0; kind < DOOR_KINDS.length; kind++) {
  for (const facing of [0, 1, 2, 3] as const) {
    for (const hinge of [0, 1] as const) {
      for (const open of [false, true]) {
        for (const top of [false, true]) {
          if (DOOR_IDS[kind][doorKey(facing, hinge, open, top)] === undefined) defDoor(kind, facing, hinge, open, top);
        }
      }
    }
  }
}

/** Bottom door halves (the half a click should also toggle above). */
export const DOOR_BOTTOM_IDS = new Set<number>();
/** Top door halves — see DOOR_BOTTOM_IDS. */
export const DOOR_TOP_IDS = new Set<number>();
/** Top half -> its bottom half (same state, for cascade pairing). */
export const DOOR_TOP_TO_BOTTOM: Record<number, number> = {};
/** Bottom half -> its top half. */
export const DOOR_BOTTOM_TO_TOP: Record<number, number> = {};
DOOR_STATE.forEach((st, id) => {
  if (!st) return;
  const other = doorId(st.kind, st.facing, st.hinge, st.open, !st.top);
  if (st.top) { DOOR_TOP_IDS.add(id); DOOR_TOP_TO_BOTTOM[id] = other; }
  else { DOOR_BOTTOM_IDS.add(id); DOOR_BOTTOM_TO_TOP[id] = other; }
  TOGGLE[id] = doorId(st.kind, st.facing, st.hinge, !st.open, st.top);
});

// --- nether dimension blocks (appended after the door states so every earlier
// id stays stable; see world/dimension.ts and worldgen/nether.ts) -------------
export const BLACKSTONE_ID = bid();
def(BLACKSTONE_ID, 'Blackstone', tsb(T.BLACKSTONE_TOP, T.BLACKSTONE, T.BLACKSTONE_TOP));
export const CRIMSON_NYLIUM_ID = bid();
def(CRIMSON_NYLIUM_ID, 'Crimson Nylium', tsb(T.CRIMSON_NYLIUM, T.CRIMSON_NYLIUM_SIDE, T.NETHERRACK));
export const WARPED_NYLIUM_ID = bid();
def(WARPED_NYLIUM_ID, 'Warped Nylium', tsb(T.WARPED_NYLIUM, T.WARPED_NYLIUM_SIDE, T.NETHERRACK));
export const CRIMSON_STEM_ID = bid();
def(CRIMSON_STEM_ID, 'Crimson Stem', tsb(T.CRIMSON_STEM_TOP, T.CRIMSON_STEM, T.CRIMSON_STEM_TOP));
export const WARPED_STEM_ID = bid();
def(WARPED_STEM_ID, 'Warped Stem', tsb(T.WARPED_STEM_TOP, T.WARPED_STEM, T.WARPED_STEM_TOP));
export const NETHER_WART_BLOCK_ID = bid();
def(NETHER_WART_BLOCK_ID, 'Nether Wart Block', all(T.NETHER_WART_BLOCK));
export const WARPED_WART_BLOCK_ID = bid();
def(WARPED_WART_BLOCK_ID, 'Warped Wart Block', all(T.WARPED_WART_BLOCK));
/** Nether portal: a non-solid, glowing cell inside a lit obsidian frame
 *  (world/portal.ts). Rendered as a full cutout cube for now. */
export const NETHER_PORTAL_ID = bid();
def(NETHER_PORTAL_ID, 'Nether Portal', all(T.NETHER_PORTAL), {
  solid: false, opaque: false, pass: Pass.Cutout, shape: RenderShape.Model, light: 11, placeable: false,
});
export const NETHER_GOLD_ORE_ID = bid();
def(NETHER_GOLD_ORE_ID, 'Nether Gold Ore', all(T.NETHER_GOLD_ORE));

// --- enchanting (appended; ids persisted) -------------------------------------
/** Enchanting table: vanilla 12/16-tall slab model (models.ts). Opens the enchanting screen. */
export const ENCHANTING_TABLE_ID = bid();
def(ENCHANTING_TABLE_ID, 'Enchanting Table', tsb(T.ENCHANTING_TABLE_TOP, T.ENCHANTING_TABLE_SIDE, T.ENCHANTING_TABLE_BOTTOM), {
  light: 7, opaque: false, pass: Pass.Cutout, shape: RenderShape.Model, collision: [[0, 0, 0, 16, 12, 16]],
});
/** Anvil: vanilla 4-part model, long axis along the facing's sideways axis. Intact /
 *  chipped / damaged, 4 facings each; facing 0 of each is the item form. */
const ANVIL_COLLISION: readonly CollisionRect[] = [[2, 0, 2, 14, 4, 14], [4, 4, 3, 12, 5, 13], [6, 5, 4, 10, 10, 12], [3, 10, 0, 13, 16, 16]];
const ANVIL_COLLISION_X: readonly CollisionRect[] = ANVIL_COLLISION.map((r) => [r[2], r[1], r[0], r[5], r[4], r[3]] as CollisionRect);
const ANVIL_TOPS = [T.ANVIL_TOP, T.CHIPPED_ANVIL_TOP, T.DAMAGED_ANVIL_TOP];
const ANVIL_NAMES = ['Anvil', 'Chipped Anvil', 'Damaged Anvil'];
/** ANVIL_FAMILIES[damage][facing]. */
export const ANVIL_FAMILIES: Array<readonly [number, number, number, number]> = [];
for (let d = 0; d < 3; d++) {
  const ids: number[] = [];
  for (let f = 0; f < 4; f++) {
    const id = bid();
    ids.push(id);
    def(id, ANVIL_NAMES[d], tsb(ANVIL_TOPS[d], T.ANVIL, T.ANVIL), {
      opaque: false, pass: Pass.Cutout, shape: RenderShape.Model,
      collision: f & 1 ? ANVIL_COLLISION_X : ANVIL_COLLISION, placeable: f === 0,
    });
  }
  ANVIL_FAMILIES.push(ids as unknown as readonly [number, number, number, number]);
}
FACING_FAMILIES.push(...ANVIL_FAMILIES);
export const ANVIL_ID = ANVIL_FAMILIES[0][0];
/** Anvil block id -> [damage 0..2, facing 0..3], or null. */
export function anvilState(id: number): [number, number] | null {
  for (let d = 0; d < 3; d++) { const f = ANVIL_FAMILIES[d].indexOf(id); if (f >= 0) return [d, f]; }
  return null;
}
/** Smithing table (full cube). */
export const SMITHING_TABLE_ID = bid();
def(SMITHING_TABLE_ID, 'Smithing Table', [T.SMITHING_TABLE_SIDE, T.SMITHING_TABLE_SIDE, T.SMITHING_TABLE_TOP, T.SMITHING_TABLE_BOTTOM, T.SMITHING_TABLE_FRONT, T.SMITHING_TABLE_FRONT]);

// --- nether, round 2 (appended at the end so earlier ids stay stable) ---------
/** Portal pane along Z (the x-axis pane is NETHER_PORTAL_ID). models.ts draws both 6/16 thick. */
export const NETHER_PORTAL_Z_ID = bid();
def(NETHER_PORTAL_Z_ID, 'Nether Portal', all(T.NETHER_PORTAL), {
  solid: false, opaque: false, pass: Pass.Cutout, shape: RenderShape.Model, light: 11, placeable: false,
});
/** Nether wart crop, ages 0..3 (textures stage0, stage1 x2, stage2). Grows on soul sand. */
export const NETHER_WART_IDS: number[] = [];
for (let age = 0; age < 4; age++) {
  const id = bid();
  NETHER_WART_IDS.push(id);
  const tile = age === 0 ? T.NETHER_WART_0 : age < 3 ? T.NETHER_WART_1 : T.NETHER_WART_2;
  def(id, 'Nether Wart', all(tile), { ...FLORA, rigid: true, placeable: age === 0 });
}
export const CRIMSON_FUNGUS_ID = bid();
def(CRIMSON_FUNGUS_ID, 'Crimson Fungus', all(T.CRIMSON_FUNGUS), FLORA);
export const WARPED_FUNGUS_ID = bid();
def(WARPED_FUNGUS_ID, 'Warped Fungus', all(T.WARPED_FUNGUS), FLORA);
export const CRIMSON_ROOTS_ID = bid();
def(CRIMSON_ROOTS_ID, 'Crimson Roots', all(T.CRIMSON_ROOTS), FLORA);
export const WARPED_ROOTS_ID = bid();
def(WARPED_ROOTS_ID, 'Warped Roots', all(T.WARPED_ROOTS), FLORA);
const VINE = { solid: false, opaque: false, pass: Pass.Cutout, shape: RenderShape.Cross, rigid: true } as const;
export const WEEPING_VINES_ID = bid();
def(WEEPING_VINES_ID, 'Weeping Vines', all(T.WEEPING_VINES), VINE);
export const WEEPING_VINES_PLANT_ID = bid();
def(WEEPING_VINES_PLANT_ID, 'Weeping Vines Plant', all(T.WEEPING_VINES_PLANT), { ...VINE, placeable: false });
export const TWISTING_VINES_ID = bid();
def(TWISTING_VINES_ID, 'Twisting Vines', all(T.TWISTING_VINES), VINE);
export const TWISTING_VINES_PLANT_ID = bid();
def(TWISTING_VINES_PLANT_ID, 'Twisting Vines Plant', all(T.TWISTING_VINES_PLANT), { ...VINE, placeable: false });
/** Soul fire: blue flame on soul sand / soul soil (animated fire planes, sheets 3/4 of render/fire.ts). */
export const SOUL_FIRE_ID = bid();
def(SOUL_FIRE_ID, 'Soul Fire', all(T.SOUL_FIRE), {
  solid: false, opaque: false, pass: Pass.Cutout, shape: RenderShape.Fire, needsSupport: true, light: 10, placeable: false,
});
/** Blaze spawner (fortress). entities/nether/spawner.ts makes it spawn blazes. */
export const SPAWNER_ID = bid();
def(SPAWNER_ID, 'Spawner', all(T.SPAWNER), { opaque: false, pass: Pass.Cutout, placeable: false });
export const CHISELED_NETHER_BRICKS_ID = bid();
def(CHISELED_NETHER_BRICKS_ID, 'Chiseled Nether Bricks', all(T.CHISELED_NETHER_BRICKS));
export const CRACKED_NETHER_BRICKS_ID = bid();
def(CRACKED_NETHER_BRICKS_ID, 'Cracked Nether Bricks', all(T.CRACKED_NETHER_BRICKS));
// --- farming (world/crops.ts) — appended: block ids are persisted in saves ---
const stageTiles = (k: string, n: number): number[] => Array.from({ length: n }, (_, i) => (T as Record<string, number>)[`${k}_S${i}`]);
export const CROP_TILES = {
  wheat: stageTiles('WHEAT', 7), carrots: stageTiles('CARROTS', 4), potatoes: stageTiles('POTATOES', 4), beetroots: stageTiles('BEETROOTS', 4),
} as const;
const CROP = { ...FLORA, placeable: false } as const;
/** Wheat ages 0..6 (age 7 is the original WHEAT_ID). */
/** Dry farmland (no water within 4 blocks); FARMLAND_ID is the moist state. */
export const FARMLAND_DRY_ID = bid();
def(FARMLAND_DRY_ID, 'Farmland', tsb(T.FARMLAND_DRY, T.DIRT, T.DIRT), { ...FARMLAND_OPTS, placeable: false });
export const WHEAT_STAGE_IDS: readonly number[] = CROP_TILES.wheat.map((tile, i) => {
  const id = bid(); def(id, 'Wheat Crops', all(tile), CROP); void i; return id;
});
/** Carrots / potatoes ages 0..7 (vanilla draws stages 0-1, 2-3, 4-6, 7 with 4 textures). */
const CROP_TEX8 = [0, 0, 1, 1, 2, 2, 2, 3];
export const CARROT_STAGE_IDS: readonly number[] = CROP_TEX8.map((k) => { const id = bid(); def(id, 'Carrots', all(CROP_TILES.carrots[k]), CROP); return id; });
export const POTATO_STAGE_IDS: readonly number[] = CROP_TEX8.map((k) => { const id = bid(); def(id, 'Potatoes', all(CROP_TILES.potatoes[k]), CROP); return id; });
/** Beetroots ages 0..3. */
export const BEETROOT_STAGE_IDS: readonly number[] = CROP_TILES.beetroots.map((tile) => { const id = bid(); def(id, 'Beetroots', all(tile), CROP); return id; });
/** Climbable cells (vines). */
export const CLIMBABLE_NETHER = new Set<number>([WEEPING_VINES_ID, WEEPING_VINES_PLANT_ID, TWISTING_VINES_ID, TWISTING_VINES_PLANT_ID]);
export const isNetherPortal = (id: number): boolean => id === NETHER_PORTAL_ID || id === NETHER_PORTAL_Z_ID;

// --- flowing lava (world/lava.ts): levels 1..7 plus falling lava ---------------
const LAVA_FLOW = {
  solid: false, light: 15, opaque: false, pass: Pass.Translucent, placeable: false,
} as const;
/** FLOWING_LAVA_IDS[level - 1], level 1..7 (the source is LAVA_ID, level 8). */
export const FLOWING_LAVA_IDS: number[] = [];
for (let lvl = 1; lvl <= 7; lvl++) {
  const id = bid();
  FLOWING_LAVA_IDS.push(id);
  def(id, 'Flowing Lava', all(T.LAVA), { ...LAVA_FLOW, surfaceDrop: 1 - lvl / 9 });
}
/** Lava falling straight down (full cell, fed from above). */
export const FALLING_LAVA_ID = bid();
def(FALLING_LAVA_ID, 'Falling Lava', all(T.LAVA), LAVA_FLOW);

// --- nether fortress pieces ------------------------------------------------------
/** Nether brick fence: post + arms toward solid neighbours / other fences (models.ts). */
export const NETHER_BRICK_FENCE_ID = bid();
def(NETHER_BRICK_FENCE_ID, 'Nether Brick Fence', all(T.NETHER_BRICKS), {
  opaque: false, pass: Pass.Cutout, shape: RenderShape.Model, collision: [[6, 0, 6, 10, 16, 10]],
});
/** Nether brick stairs, ascending toward facing 0..3 (+Z, +X, -Z, -X). */
export const NETHER_BRICK_STAIRS_IDS: number[] = [];
const STAIR_STEP: CollisionRect[] = [[0, 8, 8, 16, 16, 16], [8, 8, 0, 16, 16, 16], [0, 8, 0, 16, 16, 8], [0, 8, 0, 8, 16, 16]];
for (let f = 0; f < 4; f++) {
  const id = bid();
  NETHER_BRICK_STAIRS_IDS.push(id);
  def(id, 'Nether Brick Stairs', all(T.NETHER_BRICKS), {
    opaque: false, pass: Pass.Cutout, shape: RenderShape.Model, placeable: f === 0,
    collision: [[0, 0, 0, 16, 8, 16], STAIR_STEP[f]],
  });
}

// --- village workstations (entities/village: villagers claim these for a profession) ---
// Drawn as full cubes (vanilla lectern / grindstone / stonecutter / composter have custom shapes).
// Face order [+X, -X, +Y, -Y, +Z, -Z]; the "front" is +Z/-Z like the smithing table.
export const COMPOSTER_ID = bid();
def(COMPOSTER_ID, 'Composter', tsb(T.COMPOSTER_TOP, T.COMPOSTER_SIDE, T.COMPOSTER_BOTTOM));
export const LECTERN_ID = bid();
def(LECTERN_ID, 'Lectern', [T.LECTERN_SIDES, T.LECTERN_SIDES, T.LECTERN_TOP, T.LECTERN_BASE, T.LECTERN_FRONT, T.LECTERN_FRONT]);
export const BARREL_ID = bid();
def(BARREL_ID, 'Barrel', tsb(T.BARREL_TOP, T.BARREL_SIDE, T.BARREL_BOTTOM));
export const FLETCHING_TABLE_ID = bid();
def(FLETCHING_TABLE_ID, 'Fletching Table', [T.FLETCHING_TABLE_SIDE, T.FLETCHING_TABLE_SIDE, T.FLETCHING_TABLE_TOP, T.BIRCH_PLANKS, T.FLETCHING_TABLE_FRONT, T.FLETCHING_TABLE_FRONT]);
export const GRINDSTONE_ID = bid();
def(GRINDSTONE_ID, 'Grindstone', [T.GRINDSTONE_PIVOT, T.GRINDSTONE_PIVOT, T.GRINDSTONE_ROUND, T.GRINDSTONE_ROUND, T.GRINDSTONE_SIDE, T.GRINDSTONE_SIDE]);
export const SMOKER_ID = bid();
def(SMOKER_ID, 'Smoker', [T.SMOKER_SIDE, T.SMOKER_SIDE, T.SMOKER_TOP, T.SMOKER_BOTTOM, T.SMOKER_FRONT, T.SMOKER_SIDE]);
export const BLAST_FURNACE_ID = bid();
def(BLAST_FURNACE_ID, 'Blast Furnace', [T.BLAST_FURNACE_SIDE, T.BLAST_FURNACE_SIDE, T.BLAST_FURNACE_TOP, T.BLAST_FURNACE_TOP, T.BLAST_FURNACE_FRONT, T.BLAST_FURNACE_SIDE]);
export const STONECUTTER_ID = bid();
def(STONECUTTER_ID, 'Stonecutter', tsb(T.STONECUTTER_TOP, T.STONECUTTER_SIDE, T.STONECUTTER_BOTTOM));

// --- the End (appended: block ids are persisted in saves) ---------------------
export const END_STONE_ID = bid();
def(END_STONE_ID, 'End Stone', all(T.END_STONE));
export const END_STONE_BRICKS_ID = bid();
def(END_STONE_BRICKS_ID, 'End Stone Bricks', all(T.END_STONE_BRICKS));
/** End portal frame (vanilla 13/16-tall slab, models.ts); facing is not tracked. Unbreakable in survival. */
const END_FRAME_TILES = tsb(T.END_FRAME_TOP, T.END_FRAME_SIDE, T.END_STONE);
export const END_PORTAL_FRAME_ID = bid();
def(END_PORTAL_FRAME_ID, 'End Portal Frame', END_FRAME_TILES, {
  opaque: false, pass: Pass.Cutout, shape: RenderShape.Model, collision: [[0, 0, 0, 16, 13, 16]], light: 1,
});
/** The same frame holding an eye of ender (the eye is the extra 4/16 cube on top). */
export const END_PORTAL_FRAME_EYE_ID = bid();
def(END_PORTAL_FRAME_EYE_ID, 'End Portal Frame', END_FRAME_TILES, {
  opaque: false, pass: Pass.Cutout, shape: RenderShape.Model, collision: [[0, 0, 0, 16, 13, 16]], light: 1, placeable: false,
});
/** End portal: a non-solid 12/16-tall pane of starfield filling a completed frame (light 15). */
export const END_PORTAL_ID = bid();
def(END_PORTAL_ID, 'End Portal', all(T.END_PORTAL), {
  solid: false, opaque: false, pass: Pass.Cutout, shape: RenderShape.Model, light: 15, placeable: false,
});
export const DRAGON_EGG_ID = bid();
def(DRAGON_EGG_ID, 'Dragon Egg', all(T.DRAGON_EGG), { light: 1 });
export const isEndPortalFrame = (id: number): boolean => id === END_PORTAL_FRAME_ID || id === END_PORTAL_FRAME_EYE_ID;

// --- brewing (game/brewing): the stand is a cross-drawn sprite with the real base + rod collision ---
export const BREWING_STAND_ID = bid();
def(BREWING_STAND_ID, 'Brewing Stand', all(T.BREWING_STAND), {
  ...FLORA, rigid: true, light: 1, collision: [[1, 0, 1, 15, 2, 15], [7, 2, 7, 9, 14, 9]],
});

// --- structures round 2 (appended: block ids are persisted in saves) ------------------
/** Iron bars: connecting pane (models.ts). Collision is the full plus so a cage built of them holds. */
export const IRON_BARS_ID = bid();
def(IRON_BARS_ID, 'Iron Bars', all(T.IRON_BARS), {
  opaque: false, pass: Pass.Cutout, shape: RenderShape.Model, collision: [[7, 0, 0, 9, 16, 16], [0, 0, 7, 16, 16, 9]],
});
export const POLISHED_BLACKSTONE_BRICKS_ID = bid();
def(POLISHED_BLACKSTONE_BRICKS_ID, 'Polished Blackstone Bricks', all(T.POLISHED_BLACKSTONE_BRICKS));
export const GILDED_BLACKSTONE_ID = bid();
def(GILDED_BLACKSTONE_ID, 'Gilded Blackstone', all(T.GILDED_BLACKSTONE));
/** Trial spawner (game/trials.ts runs it): drawn like the cage spawner but never placeable. */
export const TRIAL_SPAWNER_ID = bid();
def(TRIAL_SPAWNER_ID, 'Trial Spawner', all(T.TRIAL_SPAWNER), { opaque: false, pass: Pass.Cutout, placeable: false });
/** Vault: opened with a trial key (game/trials.ts). */
export const VAULT_ID = bid();
def(VAULT_ID, 'Vault', all(T.VAULT), { placeable: false, light: 6 });
/** A vault that has paid out (game/trials.ts swaps it in, so the state persists in the world's edits). */
export const VAULT_OPENED_ID = bid();
def(VAULT_OPENED_ID, 'Vault', all(T.VAULT), { placeable: false });
/** Chorus plant (stem) and flower: end outer islands (worldgen/end.ts). Models in models.ts. */
export const CHORUS_PLANT_ID = bid();
def(CHORUS_PLANT_ID, 'Chorus Plant', all(T.CHORUS_PLANT), { opaque: false, pass: Pass.Cutout, shape: RenderShape.Model, collision: [[4, 0, 4, 12, 16, 12]] });
export const CHORUS_FLOWER_ID = bid();
def(CHORUS_FLOWER_ID, 'Chorus Flower', all(T.CHORUS_FLOWER), { opaque: false, pass: Pass.Cutout, shape: RenderShape.Model, collision: [[2, 0, 2, 14, 14, 14]] });
export const PURPUR_BLOCK_ID = bid();
def(PURPUR_BLOCK_ID, 'Purpur Block', all(T.PURPUR));
export const PURPUR_PILLAR_ID = bid();
def(PURPUR_PILLAR_ID, 'Purpur Pillar', tsb(T.PURPUR_PILLAR, T.PURPUR_PILLAR, T.PURPUR_PILLAR));
/** End gateway: the beam-less portal pane after the dragon dies (teleports to the outer islands). */
export const END_GATEWAY_ID = bid();
def(END_GATEWAY_ID, 'End Gateway', all(T.END_PORTAL), { solid: false, opaque: false, pass: Pass.Cutout, shape: RenderShape.Model, light: 15, placeable: false });

/** Silverfish spawner (stronghold portal room); game/trials.ts spawns silverfish from it. */
export const SILVERFISH_SPAWNER_ID = bid();
def(SILVERFISH_SPAWNER_ID, 'Silverfish Spawner', all(T.SPAWNER), { opaque: false, pass: Pass.Cutout, placeable: false });

// --- shaped blocks: slabs, stairs, fences, gates, walls, panes, trapdoors, more doors ------------
// Every state is its own id (like chests and doors), so collision stays a static per-id table.
// Geometry and the stair / connection rules live in shapes.ts. Appended at the end of the registry.
export interface ShapeFamily {
  kind: ShapeKind;
  name: string;
  /** Block whose tiles it wears (also what mining copies its hardness from). */
  base: number;
  tiles: Six;
  /** State-key -> id (see shapeKey). */
  ids: number[];
  /** The id the inventory carries; placement picks the state. */
  canon: number;
  /** Fences: wooden fences join each other, nether brick fences only themselves. */
  wooden: boolean;
  /** Slabs: the full-block id two slabs make. */
  double: number;
}
export const SHAPE_FAMILIES: ShapeFamily[] = [];
/** Shape state per block id (undefined for everything else). */
export const SHAPE_STATE: (ShapeState | undefined)[] = [];
/** Block ids of every state of a slab / stair (they filter light fully, like vanilla). */
export const SHAPE_BORROW_LIGHT: number[] = [];

const WOODS: ReadonlyArray<readonly [string, string]> = [
  ['Oak', 'Oak Planks'], ['Spruce', 'Spruce Planks'], ['Birch', 'Birch Planks'], ['Jungle', 'Jungle Planks'],
  ['Acacia', 'Acacia Planks'], ['Dark Oak', 'Dark Oak Planks'], ['Mangrove', 'Mangrove Planks'],
  ['Cherry', 'Cherry Planks'], ['Pale Oak', 'Pale Oak Planks'],
];
/** [display name, base block, flags]: s = slab, t = stairs, w = wall. */
const STONES: ReadonlyArray<readonly [string, string, string]> = [
  ['Cobblestone', 'Cobblestone', 'stw'], ['Mossy Cobblestone', 'Mossy Cobblestone', 'stw'], ['Stone', 'Stone', 'st'],
  ['Smooth Stone', 'Smooth Stone', 's'], ['Stone Brick', 'Stone Bricks', 'stw'], ['Mossy Stone Brick', 'Mossy Stone Bricks', 'stw'],
  ['Sandstone', 'Sandstone', 'stw'], ['Smooth Sandstone', 'Smooth Sandstone', 'st'], ['Red Sandstone', 'Red Sandstone', 'stw'],
  ['Smooth Red Sandstone', 'Smooth Red Sandstone', 'st'], ['Brick', 'Bricks', 'stw'], ['Nether Brick', 'Nether Bricks', 'stw'],
  ['Quartz', 'Quartz Block', 'st'], ['Purpur', 'Purpur Block', 'st'], ['Blackstone', 'Blackstone', 'stw'],
  ['Polished Blackstone Brick', 'Polished Blackstone Bricks', 'stw'], ['Cobbled Deepslate', 'Cobbled Deepslate', 'stw'],
  ['Polished Deepslate', 'Polished Deepslate', 'stw'], ['Deepslate Brick', 'Deepslate Bricks', 'stw'],
  ['Deepslate Tile', 'Deepslate Tiles', 'stw'], ['End Stone Brick', 'End Stone Bricks', 'stw'], ['Granite', 'Granite', 'stw'],
  ['Diorite', 'Diorite', 'stw'], ['Andesite', 'Andesite', 'stw'], ['Mud Brick', 'Mud Bricks', 'stw'],
  ['Prismarine', 'Prismarine', 'stw'], ['Prismarine Brick', 'Prismarine Bricks', 'st'],
  ['Cut Copper', 'Cut Copper', 'st'], ['Exposed Cut Copper', 'Exposed Copper', 'st'],
  ['Weathered Cut Copper', 'Weathered Copper', 'st'], ['Oxidized Cut Copper', 'Oxidized Copper', 'st'],
];
const baseIdOf = (name: string): number => B.find((b) => b?.name === name)?.id ?? -1;

function newFamily(kind: ShapeKind, name: string, base: number, tiles: Six, wooden = true): ShapeFamily {
  const fam: ShapeFamily = { kind, name, base, tiles, ids: [], canon: -1, wooden, double: -1 };
  SHAPE_FAMILIES.push(fam);
  return fam;
}
const blankState = (kind: ShapeKind, fam: number): ShapeState =>
  ({ kind, fam, half: 0, facing: 0, shape: 0, mask: 0, open: false });

function defShape(fam: ShapeFamily, st: ShapeState, key: number, canonical: boolean): number {
  const id = bid();
  const swings = st.kind === 'gate' || st.kind === 'trapdoor';
  const blocksLight = st.kind === 'slab' || st.kind === 'stair';
  def(id, fam.name, fam.tiles, {
    solid: !(swings && st.open), opaque: false, pass: Pass.Cutout, shape: RenderShape.Model,
    filter: blocksLight ? 15 : 0, collision: stateCollision(st), placeable: canonical,
  });
  fam.ids[key] = id;
  SHAPE_STATE[id] = st;
  if (blocksLight) SHAPE_BORROW_LIGHT.push(id);
  if (canonical) fam.canon = id;
  return id;
}

/** State key within a family (inverse of the layouts used below). */
export function shapeKey(s: ShapeState): number {
  switch (s.kind) {
    case 'slab': return s.half;
    case 'stair': return (s.facing * 2 + s.half) * 5 + s.shape;
    case 'gate': return s.facing * 2 + (s.open ? 1 : 0);
    case 'trapdoor': return (s.facing * 2 + s.half) * 2 + (s.open ? 1 : 0);
    default: return s.mask;
  }
}
/** The id of a family's state. */
export function shapeId(fam: ShapeFamily, patch: Partial<ShapeState>): number {
  return fam.ids[shapeKey({ ...blankState(fam.kind, 0), ...patch })];
}

// double slabs first: they are plain cubes and keep the low ids
const slabFamilies: ShapeFamily[] = [];
function slabFamily(display: string, baseName: string): void {
  const base = baseIdOf(baseName);
  if (base < 0) return;
  const fam = newFamily('slab', `${display} Slab`, base, B[base].tiles);
  fam.double = bid();
  def(fam.double, fam.name, fam.tiles, { placeable: false });
  slabFamilies.push(fam);
}
for (const [n, p] of WOODS) slabFamily(n, p);
for (const [n, b, f] of STONES) if (f.includes('s')) slabFamily(n, b);
for (const fam of slabFamilies) {
  for (const half of [0, 1, 2]) {
    if (half === 2) { fam.ids[2] = fam.double; SHAPE_STATE[fam.double] = { ...blankState('slab', SHAPE_FAMILIES.indexOf(fam)), half: 2 }; continue; }
    defShape(fam, { ...blankState('slab', SHAPE_FAMILIES.indexOf(fam)), half }, half, half === 0);
  }
}

function stairFamily(display: string, baseName: string): void {
  const base = baseIdOf(baseName);
  if (base < 0) return;
  const fam = newFamily('stair', `${display} Stairs`, base, B[base].tiles);
  const fi = SHAPE_FAMILIES.length - 1;
  for (let facing = 0; facing < 4; facing++) {
    for (let half = 0; half < 2; half++) {
      for (let shape = 0; shape < 5; shape++) {
        const st: ShapeState = { ...blankState('stair', fi), facing, half, shape };
        defShape(fam, st, shapeKey(st), facing === 0 && half === 0 && shape === 0);
      }
    }
  }
}
for (const [n, p] of WOODS) stairFamily(n, p);
for (const [n, b, f] of STONES) if (f.includes('t')) stairFamily(n, b);

function maskFamily(kind: 'fence' | 'wall' | 'pane', name: string, base: number, tiles: Six, wooden = true): void {
  const fam = newFamily(kind, name, base, tiles, wooden);
  const fi = SHAPE_FAMILIES.length - 1;
  for (let mask = 0; mask < 16; mask++) defShape(fam, { ...blankState(kind, fi), mask }, mask, mask === 3);
}
for (const [n, p] of WOODS) { const b = baseIdOf(p); if (b >= 0) maskFamily('fence', `${n} Fence`, b, B[b].tiles); }
maskFamily('fence', 'Nether Brick Fence', baseIdOf('Nether Bricks'), B[baseIdOf('Nether Bricks')].tiles, false);
for (const [n, b, f] of STONES) if (f.includes('w') && baseIdOf(b) >= 0) maskFamily('wall', `${n} Wall`, baseIdOf(b), B[baseIdOf(b)].tiles);
maskFamily('pane', 'Glass Pane', GLASS_ID, all(T.GLASS));
maskFamily('pane', 'Iron Bars', GLASS_ID, all(T.IRON_BARS));

for (const [n, p] of WOODS) {
  const b = baseIdOf(p);
  if (b < 0) continue;
  const gate = newFamily('gate', `${n} Fence Gate`, b, B[b].tiles);
  const gi = SHAPE_FAMILIES.length - 1;
  for (let facing = 0; facing < 4; facing++) {
    for (const open of [false, true]) {
      const st: ShapeState = { ...blankState('gate', gi), facing, open };
      defShape(gate, st, shapeKey(st), facing === 0 && !open);
    }
  }
}
WOODS.forEach(([n, p], wi) => {
  const b = baseIdOf(p);
  if (b < 0) return;
  const trap = newFamily('trapdoor', `${n} Trapdoor`, b, all(TRAPDOOR_TILES[wi]));
  const ti = SHAPE_FAMILIES.length - 1;
  for (let facing = 0; facing < 4; facing++) {
    for (let half = 0; half < 2; half++) {
      for (const open of [false, true]) {
        const st: ShapeState = { ...blankState('trapdoor', ti), facing, half, open };
        defShape(trap, st, shapeKey(st), facing === 0 && half === 0 && !open);
      }
    }
  }
});
// gates and trapdoors toggle on right-click
for (const st of SHAPE_STATE) {
  if (!st || (st.kind !== 'gate' && st.kind !== 'trapdoor')) continue;
  const fam = SHAPE_FAMILIES[st.fam];
  TOGGLE[fam.ids[shapeKey(st)]] = fam.ids[shapeKey({ ...st, open: !st.open })];
}

/** The ids that existed before the shape families: converted in place by reshapeId. */
const LEGACY_FENCE = NETHER_BRICK_FENCE_ID;
const LEGACY_BARS = IRON_BARS_ID;
for (const id of [LEGACY_FENCE, ...NETHER_BRICK_STAIRS_IDS, LEGACY_BARS]) B[id].placeable = false;
const famNamed = (name: string): number => SHAPE_FAMILIES.findIndex((f) => f.name === name);
const legacyState = (id: number): ShapeState | undefined => {
  if (id === LEGACY_FENCE) return { ...blankState('fence', famNamed('Nether Brick Fence')) };
  if (id === LEGACY_BARS) return { ...blankState('pane', famNamed('Iron Bars')) };
  const f = NETHER_BRICK_STAIRS_IDS.indexOf(id);
  return f >= 0 ? { ...blankState('stair', famNamed('Nether Brick Stairs')), facing: f } : undefined;
};

/** The same state with a new id's worth of neighbours: stairs re-pick their corner shape and
 *  fences / walls / panes their arms; legacy fence / stairs / bars ids are converted to the families. */
export function reshapeId(id: number, get: (dx: number, dz: number) => number): number {
  const s = SHAPE_STATE[id] ?? legacyState(id);
  if (!s) return id;
  const fam = SHAPE_FAMILIES[s.fam];
  if (s.kind === 'stair') {
    const nb = (dx: number, dz: number): StairNb => {
      const o = SHAPE_STATE[get(dx, dz)] ?? legacyState(get(dx, dz));
      return o && o.kind === 'stair' ? { facing: o.facing, half: o.half } : null;
    };
    return fam.ids[shapeKey({ ...s, shape: stairShape(s.facing, s.half, nb) })];
  }
  if (s.kind !== 'fence' && s.kind !== 'wall' && s.kind !== 'pane') return id;
  let mask = 0;
  for (const [dx, dz, bit] of DIR_BITS) {
    const n = get(dx, dz);
    const o = SHAPE_STATE[n] ?? legacyState(n);
    let join = IS_OPAQUE[n] === 1 && RENDER_SHAPE[n] === RenderShape.Cube;
    if (s.kind === 'pane' && n === GLASS_ID) join = true;
    if (o) {
      const ofam = SHAPE_FAMILIES[o.fam];
      if (o.kind === 'gate') join = join || ((o.facing & 1) === 0) === (dx !== 0);
      else if (s.kind === 'fence') join = join || (o.kind === 'fence' && ofam.wooden === fam.wooden);
      else if (s.kind === 'wall') join = join || o.kind === 'wall';
      else join = join || o.kind === 'pane' || o.kind === 'wall';
    }
    if (join) mask |= bit;
  }
  return fam.ids[mask];
}

// more door woods: the shaped-block material list above, wearing their planks until door art exists
{
  const firstNew = B.length;
  for (const [n, p] of WOODS.slice(2)) {
    const b = baseIdOf(p);
    if (b < 0) continue;
    (DOOR_KINDS as unknown as Array<{ name: string; bottom: number; top: number }>)
      .push({ name: `${n} Door`, bottom: B[b].tiles[0], top: B[b].tiles[0] });
    DOOR_IDS.push([]);
    const kind = DOOR_KINDS.length - 1;
    for (const facing of [0, 1, 2, 3] as const) for (const hinge of [0, 1] as const)
      for (const open of [false, true]) for (const top of [false, true]) defDoor(kind, facing, hinge, open, top);
  }
  for (let id = firstNew; id < B.length; id++) {
    const st = DOOR_STATE[id];
    if (!st) continue;
    const other = doorId(st.kind, st.facing, st.hinge, st.open, !st.top);
    if (st.top) { DOOR_TOP_IDS.add(id); DOOR_TOP_TO_BOTTOM[id] = other; }
    else { DOOR_BOTTOM_IDS.add(id); DOOR_BOTTOM_TO_TOP[id] = other; }
    TOGGLE[id] = doorId(st.kind, st.facing, st.hinge, !st.open, st.top);
  }
}

// --- furnace-likes: facing x lit (ids appended; the legacy ids are the unlit +Z variants) ------
type Quad = readonly [number, number, number, number];
interface FurnaceKind { name: string; unlit: Quad; lit: Quad }
/** Furnace, blast furnace, smoker, each with its four facings unlit and lit. Lit variants
 *  glow (light 13), show the burning front, and drop / pick as the unlit +Z item. */
export const FURNACE_KINDS: FurnaceKind[] = [];
const FURNACE_KIND_OF = new Map<number, FurnaceKind>();
export const FURNACE_LIT_LIGHT = 13;
function defFurnaceKind(
  name: string, legacy: number, extra: Quad | null,
  front: number, frontOn: number, side: number, top: number, bottom: number,
): void {
  const faced = (fr: number, f: 0 | 1 | 2 | 3): Six => {
    const tl = frontFaced(fr, side, top, f);
    tl[3] = bottom;
    return tl;
  };
  const unlit: number[] = [legacy];
  for (let f = 1; f < 4; f++) {
    if (extra) unlit.push(extra[f]);
    else { const id = bid(); def(id, name, faced(front, f as 1 | 2 | 3), { placeable: false }); unlit.push(id); }
  }
  const lit: number[] = [];
  for (let f = 0; f < 4; f++) {
    const id = bid();
    def(id, name, faced(frontOn, f as 0 | 1 | 2 | 3), { placeable: false, light: FURNACE_LIT_LIGHT });
    lit.push(id);
  }
  const kind: FurnaceKind = { name, unlit: unlit as unknown as Quad, lit: lit as unknown as Quad };
  FURNACE_KINDS.push(kind);
  for (const id of [...unlit, ...lit]) FURNACE_KIND_OF.set(id, kind);
}
defFurnaceKind('Furnace', FURNACE_FACING_0, [FURNACE_FACING_0, FURNACE_FACING_1, FURNACE_FACING_2, FURNACE_FACING_3],
  T.FURNACE_FRONT, T.FURNACE_FRONT_ON, T.FURNACE_SIDE, T.FURNACE_TOP, T.FURNACE_TOP);
defFurnaceKind('Blast Furnace', BLAST_FURNACE_ID, null,
  T.BLAST_FURNACE_FRONT, T.BLAST_FURNACE_FRONT_ON, T.BLAST_FURNACE_SIDE, T.BLAST_FURNACE_TOP, T.BLAST_FURNACE_TOP);
defFurnaceKind('Smoker', SMOKER_ID, null,
  T.SMOKER_FRONT, T.SMOKER_FRONT_ON, T.SMOKER_SIDE, T.SMOKER_TOP, T.SMOKER_BOTTOM);
FACING_FAMILIES.push(FURNACE_KINDS[1].unlit, FURNACE_KINDS[2].unlit);

// --- sculk shrieker + catalyst (appended: block ids are persisted in saves) ------------------
/** Shrieker a player placed: shrieks and darkens, never summons (vanilla can_summon=false). */
export const SCULK_SHRIEKER_ID = bid();
def(SCULK_SHRIEKER_ID, 'Sculk Shrieker', [T.SHRIEKER_SIDE, T.SHRIEKER_SIDE, T.SHRIEKER_TOP, T.SHRIEKER_BOTTOM, T.SHRIEKER_SIDE, T.SHRIEKER_SIDE]);
/** Worldgen shrieker (can_summon=true): four warnings summon a warden. Mines to the plain item. */
export const SCULK_SHRIEKER_SUMMON_ID = bid();
def(SCULK_SHRIEKER_SUMMON_ID, 'Sculk Shrieker', [T.SHRIEKER_SIDE, T.SHRIEKER_SIDE, T.SHRIEKER_SUMMON_TOP, T.SHRIEKER_BOTTOM, T.SHRIEKER_SIDE, T.SHRIEKER_SIDE], { placeable: false });
export const SCULK_CATALYST_ID = bid();
def(SCULK_CATALYST_ID, 'Sculk Catalyst', [T.CATALYST_SIDE, T.CATALYST_SIDE, T.CATALYST_TOP, T.CATALYST_BOTTOM, T.CATALYST_SIDE, T.CATALYST_SIDE], { light: 6 });
/** True for any furnace / blast furnace / smoker id, lit or not, in any facing. */
export function isFurnaceBlock(id: number): boolean { return FURNACE_KIND_OF.has(id); }
export function isFurnaceLit(id: number): boolean {
  const k = FURNACE_KIND_OF.get(id);
  return !!k && k.lit.indexOf(id) >= 0;
}
/** Facing 0..3 of a furnace-like id, or -1. */
export function furnaceFacingOf(id: number): number {
  const k = FURNACE_KIND_OF.get(id);
  if (!k) return -1;
  const i = k.unlit.indexOf(id);
  return i >= 0 ? i : k.lit.indexOf(id);
}
/** The same furnace, same facing, lit or unlit (`id` itself if it is not one). */
export function furnaceWithLit(id: number, lit: boolean): number {
  const k = FURNACE_KIND_OF.get(id);
  if (!k) return id;
  const f = furnaceFacingOf(id);
  return (lit ? k.lit : k.unlit)[f];
}
/** Does `family` (from facingFamilyOf) hold furnace-likes (front faces the player on placement)? */
export function isFurnaceFamily(family: Quad | null): boolean {
  return !!family && FURNACE_KIND_OF.has(family[0]);
}

// --- wither skeleton skull (appended: block ids are persisted in saves) ---------------------------
/** Wither skeleton skull: vanilla's 8x8x8 head on the floor (models.ts). Three on a soul sand T summon the wither (game/witherSummon.ts). */
export const WITHER_SKULL_ID = bid();
def(WITHER_SKULL_ID, 'Wither Skeleton Skull', [T.WITHER_SKULL_SIDE, T.WITHER_SKULL_SIDE, T.WITHER_SKULL_TOP, T.WITHER_SKULL_TOP, T.WITHER_SKULL_FACE, T.WITHER_SKULL_FACE], {
  opaque: false, pass: Pass.Cutout, shape: RenderShape.Model, collision: [[4, 0, 4, 12, 8, 12]],
});

// --- rails: one id per shape (appended: block ids are persisted in saves) -----------------------
/**
 * Shape order (vanilla RailShape): 0 north-south, 1 east-west, 2..5 ascending east / west / north / south
 * (rising towards that side), 6..9 corners south-east / south-west / north-west / north-east.
 * RAIL_ID is the plain north-south one; world/rails.ts picks the shape from the neighbours, and every
 * variant drops the one item (mining.ts canon). RAIL_FAMILIES[0] = plain (10 shapes), [1] = powered (shapes 0..5).
 */
export const RAIL_FAMILIES: number[][] = [[RAIL_ID], []];
let poweredBase = -1;
for (let fam = 0; fam < 2; fam++) {
  for (let shape = fam === 0 ? 1 : 0; shape < (fam === 0 ? 10 : 6); shape++) {
    const id = bid();
    if (fam === 1 && shape === 0) poweredBase = id;
    RAIL_FAMILIES[fam][shape] = id;
    const tile = fam === 1 ? T.POWERED_RAIL : shape >= 6 ? T.RAIL_CORNER : T.RAIL;
    def(id, fam === 1 ? 'Powered Rail' : 'Rail', all(tile), {
      solid: false, opaque: false, pass: Pass.Cutout, shape: RenderShape.Rail, needsSupport: true, placeable: id === poweredBase,
    });
  }
}
export const POWERED_RAIL_ID = poweredBase;
// the legacy RAIL_ID def (flat ground cover, shape 0) becomes the north-south rail
B[RAIL_ID] = { ...B[RAIL_ID], shape: RenderShape.Rail };

// --- redstone (world/redstone): rails that react to power, iron door / trapdoor, the components ---
/** RAIL_FAMILIES[1] is the powered rail switched off (what is placed); 2 = powered rail on, 3 / 4 = detector
 *  rail off / on, 5 / 6 = activator rail off / on. Shapes 0..5 each; world/redstone switches between them. */
([['Powered Rail', T.POWERED_RAIL_ON], ['Detector Rail', T.DETECTOR_RAIL], ['Detector Rail', T.DETECTOR_RAIL_ON],
  ['Activator Rail', T.ACTIVATOR_RAIL], ['Activator Rail', T.ACTIVATOR_RAIL_ON]] as const).forEach(([name, tile], i) => {
  const fam = i + 2;
  RAIL_FAMILIES[fam] = [];
  for (let shape = 0; shape < 6; shape++) {
    const id = bid();
    RAIL_FAMILIES[fam][shape] = id;
    def(id, name, all(tile), {
      solid: false, opaque: false, pass: Pass.Cutout, shape: RenderShape.Rail, needsSupport: true, placeable: shape === 0 && (fam === 3 || fam === 5),
    });
  }
});
export const DETECTOR_RAIL_ID = RAIL_FAMILIES[3][0];
export const ACTIVATOR_RAIL_ID = RAIL_FAMILIES[5][0];
/** Iron door / iron trapdoor: id -> the same block swung the other way. Not in TOGGLE: only redstone moves them. */
export const IRON_TOGGLE: Record<number, number> = {};
export const IRON_DOOR_ID = ((): number => {
  (DOOR_KINDS as unknown as Array<{ name: string; bottom: number; top: number }>)
    .push({ name: 'Iron Door', bottom: T.IRON_DOOR_BOTTOM, top: T.IRON_DOOR_TOP });
  DOOR_IDS.push([]);
  const kind = DOOR_KINDS.length - 1, firstNew = B.length;
  for (const facing of [0, 1, 2, 3] as const) for (const hinge of [0, 1] as const)
    for (const open of [false, true]) for (const top of [false, true]) defDoor(kind, facing, hinge, open, top);
  for (let id = firstNew; id < B.length; id++) {
    const st = DOOR_STATE[id]!;
    const other = doorId(st.kind, st.facing, st.hinge, st.open, !st.top);
    if (st.top) { DOOR_TOP_IDS.add(id); DOOR_TOP_TO_BOTTOM[id] = other; }
    else { DOOR_BOTTOM_IDS.add(id); DOOR_BOTTOM_TO_TOP[id] = other; }
    IRON_TOGGLE[id] = doorId(st.kind, st.facing, st.hinge, !st.open, st.top);
  }
  return doorId(kind, 0, 0, false, false);
})();
export const IRON_TRAPDOOR_ID = ((): number => {
  const trap = newFamily('trapdoor', 'Iron Trapdoor', baseIdOf('Block of Iron'), all(T.IRON_TRAPDOOR), false);
  const ti = SHAPE_FAMILIES.length - 1;
  for (let facing = 0; facing < 4; facing++) for (let half = 0; half < 2; half++) for (const open of [false, true]) {
    const st: ShapeState = { ...blankState('trapdoor', ti), facing, half, open };
    defShape(trap, st, shapeKey(st), facing === 0 && half === 0 && !open);
  }
  for (const id of trap.ids) {
    const st = SHAPE_STATE[id]!;
    IRON_TOGGLE[id] = trap.ids[shapeKey({ ...st, open: !st.open })];
  }
  return trap.canon;
})();
export const REDSTONE_BLOCK_ID = baseIdOf('Block of Redstone');
export const TNT_BLOCK_ID = baseIdOf('TNT');
defineRedstoneBlocks({
  bid, def, T,
  wallTorch: (id, face) => { WALL_TORCHES.push([id, face]); },
  torchWalls: (base, byFace) => { TORCH_WALLS.set(base, byFace); },
});
defineUtilityBlocks({
  bid, def, T,
  attached: (id, face) => { ATTACHED.push([id, face]); },
  attachVariants: (base, byFace) => { ATTACH_VARIANTS.set(base, byFace); },
  woods: WOODS.map(([n, p]) => [n, B[baseIdOf(p)].tiles[0]] as const),
  flora: ['Poppy', 'Dandelion', 'Blue Orchid', 'Allium', 'Azure Bluet', 'Red Tulip', 'Orange Tulip', 'White Tulip', 'Pink Tulip', 'Oxeye Daisy',
    'Cornflower', 'Lily of the Valley', 'Dead Bush', 'Red Mushroom', 'Brown Mushroom', 'Oak Sapling', 'Fern', 'Azalea', 'Flowering Azalea',
    'Bamboo', 'Crimson Fungus', 'Warped Fungus', 'Crimson Roots', 'Warped Roots']
    .map((n) => baseIdOf(n)).filter((id) => id > 0).map((id) => [id, B[id].tiles[0]] as const),
  composter: COMPOSTER_ID,
  chests: {
    closed: [CHEST_FACING_0, CHEST_FACING_1, CHEST_FACING_2, CHEST_FACING_3],
    open: [CHEST_OPEN_FACING_0, CHEST_OPEN_FACING_1, CHEST_OPEN_FACING_2, CHEST_OPEN_FACING_3],
  },
});
FACING_FAMILIES.push(...U_FACING_FAMILIES);
for (const id of U_CLIMBABLE) CLIMBABLE_NETHER.add(id);

export const BLOCKS: readonly BlockDef[] = B;

/** Fast property lookup tables (indexed by block id). */
export const IS_SOLID: Uint8Array = new Uint8Array(B.length);
export const IS_OPAQUE: Uint8Array = new Uint8Array(B.length);
export const LIGHT_EMISSION: Uint8Array = new Uint8Array(B.length);
export const LIGHT_FILTER: Uint8Array = new Uint8Array(B.length);
export const RENDER_PASS: Uint8Array = new Uint8Array(B.length);
export const RENDER_SHAPE: Uint8Array = new Uint8Array(B.length);
export const SURFACE_DROP: Float32Array = new Float32Array(B.length);
export const NEEDS_SUPPORT: Uint8Array = new Uint8Array(B.length);
/** Cells that count as water for swimming and water-face culling. */
export const IS_WATERY: Uint8Array = new Uint8Array(B.length);
/** Water amount per block id: 0 = not water, 8 = source / falling, 1..7 = flowing. */
export const WATER_AMOUNT: Uint8Array = new Uint8Array(B.length);
/** Water that never drains: source blocks and waterlogged plants. */
export const IS_WATER_SOURCE: Uint8Array = new Uint8Array(B.length);
export const IS_RIGID: Uint8Array = new Uint8Array(B.length);
/** Vanilla `crop` model (wheat, carrots, potatoes, beetroots, nether wart): four
 *  vertical planes at 4/16 and 12/16 on both axes, 1/16 sunk into the ground. */
export const IS_CROP_MODEL: Uint8Array = new Uint8Array(B.length);
for (const id of [...WHEAT_STAGE_IDS, WHEAT_ID, ...CARROT_STAGE_IDS, ...POTATO_STAGE_IDS, ...BEETROOT_STAGE_IDS, ...NETHER_WART_IDS]) IS_CROP_MODEL[id] = 1;
export const IS_TORCH: Uint8Array = new Uint8Array(B.length);
/** Blocks that fall when the cell below is free (world/falling.ts): sand, red sand, gravel, anvils, the dragon egg.
 *  (No concrete powder in this build.) */
export const IS_GRAVITY: Uint8Array = new Uint8Array(B.length);
for (const id of [SAND_ID, RED_SAND_ID, GRAVEL_ID, DRAGON_EGG_ID, ...ANVIL_FAMILIES.flat()]) IS_GRAVITY[id] = 1;
/** Flat shape: quad height above the cell bottom / texture stays axis-aligned. */
export const FLAT_Y: Float32Array = new Float32Array(B.length);
export const FLAT_ALIGNED: Uint8Array = new Uint8Array(B.length);
/** Rail blocks: shape 0..9 (see RAIL_FAMILIES), -1 for every other block; and which of the two families (0 plain, 1 powered). */
export const RAIL_SHAPE: Int8Array = new Int8Array(B.length).fill(-1);
export const RAIL_KIND: Int8Array = new Int8Array(B.length).fill(-1);
RAIL_FAMILIES.forEach((ids, kind) => ids.forEach((id, shape) => { RAIL_SHAPE[id] = shape; RAIL_KIND[id] = kind; }));

/** Per-block collision boxes in 1/16 units, six per box (x0,y0,z0,x1,y1,z1), or
 *  null for the default: the whole cell when the block is solid, nothing when
 *  it is not (see `collisionOf`). */
export const COLLISION_BOXES: readonly (Float32Array | null)[] = new Array(B.length).fill(null);
/** The whole cell — what a plain solid block collides with. */
export const FULL_CELL_BOXES = new Float32Array([0, 0, 0, 16, 16, 16]);
/** Nothing at all (air, plants, an open door). */
export const NO_BOXES = new Float32Array(0);

for (let i = 0; i < B.length; i++) {
  const b = B[i];
  if (!b) continue;
  IS_SOLID[i] = b.solid ? 1 : 0;
  IS_OPAQUE[i] = b.opaque ? 1 : 0;
  LIGHT_EMISSION[i] = b.light;
  LIGHT_FILTER[i] = b.filter;
  RENDER_PASS[i] = b.pass;
  RENDER_SHAPE[i] = b.shape;
  SURFACE_DROP[i] = b.surfaceDrop ?? 0;
  NEEDS_SUPPORT[i] = b.needsSupport ? 1 : 0;
  IS_WATER_SOURCE[i] = b.waterlogged || i === WATER_ID ? 1 : 0;
  WATER_AMOUNT[i] = IS_WATER_SOURCE[i] === 1 || i === FALLING_WATER_ID ? 8 : Math.max(0, FLOWING_WATER_IDS.indexOf(i));
  IS_WATERY[i] = WATER_AMOUNT[i] > 0 ? 1 : 0;
  IS_RIGID[i] = b.rigid ? 1 : 0;
  IS_TORCH[i] = b.torch ? 1 : 0;
  FLAT_Y[i] = b.flatY ?? 1 / 16;
  FLAT_ALIGNED[i] = b.flatAligned ? 1 : 0;
  if (b.collision) {
    const boxes = new Float32Array(b.collision.length * 6);
    for (let k = 0; k < b.collision.length; k++) boxes.set(b.collision[k], k * 6);
    (COLLISION_BOXES as (Float32Array | null)[])[i] = boxes;
  }
}

/** What the player collides with in a cell holding `id`, in 1/16 units. */
export function collisionOf(id: number): Float32Array {
  const boxes = COLLISION_BOXES[id];
  if (boxes) return boxes;
  return IS_SOLID[id] === 1 ? FULL_CELL_BOXES : NO_BOXES;
}

// --- attachment (what holds a needs-support block up) -------------------------

/** Neighbour directions by face index, in the order the raycaster reports
 *  entry faces: +X, -X, +Y, -Y, +Z, -Z. */
export const FACE_DIRS: ReadonlyArray<readonly [number, number, number]> = [
  [1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1],
];
/** Face index of the block below — what floor-standing blocks sit on. */
export const FACE_FLOOR = 3;
const FACE_FLOOR_BIT = 1 << FACE_FLOOR;

/**
 * For a torch, the one face it hangs on (-1 = it stands on the floor). Set from
 * the wall ids, so the mesher and the support rule never have to guess which of
 * several solid neighbours the player actually clicked.
 */
export const TORCH_FACING: Int8Array = new Int8Array(B.length).fill(-1);
for (const [id, face] of WALL_TORCHES) TORCH_FACING[id] = face;

/**
 * Face-index bitmask of the neighbours that can hold a needs-support block up.
 * Floor flora and cacti only ever stand on the cell below; a torch stands on a
 * floor or sticks to the one wall it was placed against — never a ceiling.
 */
export const SUPPORT_FACES: Uint8Array = new Uint8Array(B.length);
for (let i = 0; i < B.length; i++) {
  const b = B[i];
  if (!b?.needsSupport) continue;
  SUPPORT_FACES[i] = TORCH_FACING[i] >= 0 ? 1 << TORCH_FACING[i] : FACE_FLOOR_BIT;
}
for (const [id, face] of RS_ATTACH) SUPPORT_FACES[id] = 1 << face; // levers, buttons, tripwire hooks: any face
/** For an attached non-torch block (ATTACHED), the face of the neighbour that holds it; -1 for everything else. */
export const ATTACHED_FACE: Int8Array = new Int8Array(B.length).fill(-1);
for (const [id, face] of ATTACHED) { ATTACHED_FACE[id] = face; if (B[id]?.needsSupport) SUPPORT_FACES[id] = 1 << face; }

/** Face index of a unit axis direction (nx, ny, nz), or -1 if it is not one. */
export function faceIndex(nx: number, ny: number, nz: number): number {
  if (Math.abs(nx) + Math.abs(ny) + Math.abs(nz) !== 1) return -1;
  if (nx !== 0) return nx > 0 ? 0 : 1;
  if (ny !== 0) return ny > 0 ? 2 : 3;
  return nz > 0 ? 4 : 5;
}

/** Which of a cell's 6 neighbours are solid, as a SUPPORT_FACES-style bitmask. */
export function solidNeighborMask(isSolid: (dx: number, dy: number, dz: number) => boolean): number {
  let mask = 0;
  for (let i = 0; i < 6; i++) {
    const d = FACE_DIRS[i];
    if (isSolid(d[0], d[1], d[2])) mask |= 1 << i;
  }
  return mask;
}

/** Does the neighbour mask `held` (see solidNeighborMask) keep `id` up? Blocks
 *  that need no support always pass. */
export function heldBy(id: number, held: number): boolean {
  const faces = SUPPORT_FACES[id];
  return faces === 0 || (faces & held) !== 0;
}

/**
 * The id to actually place when the player picks `id` on the face a ray entered
 * (normal nx, ny, nz, pointing back at the player).
 *
 * A torch only has a mount once it knows which face it was clicked on, so the
 * standing id in the hotbar turns into the wall variant of that face — the same
 * trick as the chest facings. Everything else is placed as-is.
 */
export function placeIdFor(id: number, nx: number, ny: number, nz: number): number {
  const walls = TORCH_WALLS.get(id) ?? ATTACH_VARIANTS.get(id);
  if (walls === undefined) return id;
  // the clicked block is the neighbour opposite the entry normal, so a top face
  // (ny > 0) means "stand on the cell below" and -Y has no id at all
  const face = faceIndex(-nx, -ny, -nz);
  return face < 0 ? id : walls[face] ?? id;
}

/**
 * The neighbour of a cell that must be solid to hold `id` up, as a face index,
 * or -1 when the block needs no support at all.
 *
 * Floor flora (plants, cacti, ground cover) stands on the cell below; a torch
 * stands on the floor (the standing id) or holds on to the one wall its variant
 * was made for — never a different wall, and never a ceiling.
 */
export function supportFace(id: number): number {
  if (SUPPORT_FACES[id] === 0) return -1;
  if (ATTACHED_FACE[id] >= 0) return ATTACHED_FACE[id];
  return TORCH_FACING[id] >= 0 ? TORCH_FACING[id] : FACE_FLOOR;
}

/**
 * Can `id` (as resolved by `placeIdFor`) sit in a cell, given a neighbour
 * lookup `isSolid(dx, dy, dz)`? Blocks that need no support — every full cube —
 * are always placeable.
 */
export function canPlaceAttached(
  id: number,
  isSolid: (dx: number, dy: number, dz: number) => boolean
): boolean {
  const face = supportFace(id);
  if (face < 0) return true;
  const [dx, dy, dz] = FACE_DIRS[face];
  return isSolid(dx, dy, dz);
}

/** Blocks the raycaster can target (break/place): everything except air and
 *  liquids. Includes non-solid blocks like torches and plants so they can be
 *  clicked/broken. */
export const IS_TARGETABLE: Uint8Array = new Uint8Array(B.length);
for (let i = 0; i < B.length; i++) {
  const b = B[i];
  if (!b) continue;
  // Fire is not selectable, like vanilla: the ray passes through the flame to
  // whatever is behind it. Otherwise the block outline hangs in mid-air around
  // the flame (the plates are 1.4 blocks tall, so it pokes up against the sky)
  // and punching targets fire, which vanilla never lets you break by hand.
  IS_TARGETABLE[i] = b.pass !== Pass.Invisible && b.pass !== Pass.Translucent && i !== FIRE_ID ? 1 : 0;
}

export function tintOf(id: number): [number, number, number] {
  return B[id]?.tint ?? [1, 1, 1];
}

/**
 * Blocks fire can catch and consume (vanilla's burn odds). The wood families
 * share their display names ('Spruce Planks', 'Birch Leaves', ...), so the
 * table is built from name words instead of a flag on ~60 defs — except that
 * the grass *blocks* must be excluded (they are dirt, not dry grass).
 */
const FLAMMABLE_WORDS = [
  'Log', 'Planks', 'Leaves', 'Wool', 'Door', 'Bookshelf', 'Crafting Table',
  'Hay Bale', 'Chest', 'Sapling', 'Fern', 'Bush', 'Short Grass', 'Tall Grass',
  'Azalea', 'Vine', 'Bamboo', 'Kelp', 'Seagrass', 'Sugar Cane', 'Cobweb',
  'Sponge', 'Wheat', 'Petals', 'Litter', 'Wildflowers', 'Moss Carpet',
  'Mushroom', 'Stem', 'Poppy', 'Dandelion', 'Sunflower', 'Lilac', 'Peony',
  'Tulip', 'Orchid', 'Allium', 'Bluet', 'Cornflower', 'Daisy', 'Lily',
];
export const IS_FLAMMABLE: Uint8Array = new Uint8Array(B.length);
for (const b of B) {
  if (b && FLAMMABLE_WORDS.some((w) => b.name.includes(w))) IS_FLAMMABLE[b.id] = 1;
}

/** Blocks that keep a fire burning forever without being consumed — vanilla
 *  netherrack and magma blocks (soul sand only sustains soul fire). */
export const SUSTAINS_FIRE: Uint8Array = new Uint8Array(B.length);
for (const id of [NETHERRACK_ID, MAGMA_ID]) SUSTAINS_FIRE[id] = 1;

/**
 * Blast resistance (vanilla's per-block figure): how much of an explosion ray's
 * energy one cell of this block eats — see world/tnt.ts, which walks the rays.
 * The value decides how deep a charge cuts, so the table follows vanilla's own
 * numbers and groups the blocks a blast actually meets; anything unlisted is 0,
 * which is what vanilla gives dirt, sand, plants, fire and TNT, so those cells
 * never slow a ray down.
 *
 * First match wins, so a family entry has to come before the word inside it
 * ('Sandstone' before 'Sand', 'Blue Ice' before 'Ice', and 'Bedrock' — which
 * contains 'Bed' — before everything). Exotica that only differ by a tier or
 * two share the nearest family entry rather than growing a row each.
 */
const BLAST_RESISTANCE_BY_WORD: ReadonlyArray<readonly [number, readonly string[]]> = [
  [Number.POSITIVE_INFINITY, ['Bedrock', 'End Portal']],
  [9, ['End Stone']],
  [1200, ['Obsidian', 'Ancient Debris', 'Anvil', 'Enchanting Table']],
  [
    6, [
      'Stone', 'Cobblestone', 'Bricks', 'Deepslate', 'Tuff', 'Diorite',
      'Granite', 'Andesite', 'Prismarine', 'Ore', 'Block of Iron', 'Block of Gold',
      'Block of Diamond', 'Block of Emerald', 'Block of Redstone',
      'Block of Coal', 'Block of Copper', 'Block of Raw',
    ],
  ],
  [4.2, ['Terracotta', 'Basalt', 'Cobweb']],
  [3.5, ['Furnace']],
  [3, ['Planks', 'Door', 'Block of Lapis']],
  [2.8, ['Blue Ice']],
  [2.5, ['Chest', 'Crafting Table', 'Dried Kelp']],
  [2, ['Log', 'Wood', 'Bone Block']],
  [1.5, ['Bookshelf', 'Amethyst']],
  [1, ['Pumpkin', "Jack o'Lantern", 'Melon', 'Dripstone', 'Shroomlight']],
  [0.8, ['Sandstone', 'Wool', 'Quartz']],
  [0.75, ['Calcite']],
  [0.7, ['Rail']],
  [0.6, ['Grass Block', 'Gravel', 'Clay', 'Sponge', 'Mycelium']],
  [0.5, ['Dirt', 'Podzol', 'Sand', 'Soul Sand', 'Soul Soil', 'Ice', 'Hay Bale', 'Magma', 'Mud']],
  [0.4, ['Netherrack', 'Cactus', 'Ladder']],
  [0.3, ['Glass', 'Glowstone', 'Sea Lantern']],
  [0.2, ['Leaves', 'Bed', 'Mushroom Block']],
  [0.1, ['Snow', 'Moss Block', 'Carpet']],
];
export const BLAST_RESISTANCE: Float32Array = new Float32Array(B.length);
for (const b of B) {
  if (!b) continue;
  for (const [resistance, words] of BLAST_RESISTANCE_BY_WORD) {
    if (words.some((w) => b.name.includes(w))) {
      BLAST_RESISTANCE[b.id] = resistance;
      break;
    }
  }
}

/**
 * Tiles that receive a tint in the mesher. Mostly grayscale textures (grass
 * top, leaves, plants), but sugar_cane.png is pre-colored in vanilla AND still
 * multiplied by the biome grass tint (tinted_cross model) — so it stays listed.
 * Pre-colored tiles with no vanilla tint (grass side, cherry leaves, flowers)
 * must NOT be listed here.
 */
export const TINTABLE_TILES: Uint8Array = new Uint8Array(TILE_COUNT);
for (const tile of [
  T.GRASS_TOP, T.MYCELIUM_TOP,
  T.LEAVES, T.SPRUCE_LEAVES, T.BIRCH_LEAVES, T.JUNGLE_LEAVES, T.ACACIA_LEAVES,
  T.DARK_OAK_LEAVES, T.MANGROVE_LEAVES, T.PALE_OAK_LEAVES,
  T.TALL_GRASS, T.FERN, T.LILY_PAD, T.SUGAR_CANE, T.VINE,
]) {
  TINTABLE_TILES[tile] = 1;
}

/** Default hotbar contents (9 slots, Minecraft-style).  Beds and chests are
 *  placeable from the inventory; they appear in the hotbar via the inventory
 *  picker (E), so they don't need to be in the default 9 slots. */
export const DEFAULT_HOTBAR: number[] = [
  GRASS_ID, DIRT_ID, STONE_ID, COBBLE_ID, PLANKS_ID, LOG_ID, GLASS_ID, GLOWSTONE_ID, TORCH_ID,
];

/** Every block the player can place, in registry order (inventory contents). */
export const PLACEABLE_IDS: number[] = B.filter((b) => b?.placeable).map((b) => b.id);

/** Any lava cell (source, flowing, falling). */
export const IS_LAVA: Uint8Array = new Uint8Array(B.length);
/** Lava level: 8 for source / falling, 1..7 flowing, 0 otherwise. */
export const LAVA_AMOUNT: Uint8Array = new Uint8Array(B.length);
IS_LAVA[LAVA_ID] = 1; LAVA_AMOUNT[LAVA_ID] = 8;
IS_LAVA[FALLING_LAVA_ID] = 1; LAVA_AMOUNT[FALLING_LAVA_ID] = 8;
FLOWING_LAVA_IDS.forEach((id, i) => { IS_LAVA[id] = 1; LAVA_AMOUNT[id] = i + 1; });

/** Ids whose neighbours decide their state (stairs corners, fence / wall / pane arms, incl. the pre-family ids). */
export const NEEDS_RESHAPE: Uint8Array = new Uint8Array(B.length);
for (const st of SHAPE_STATE.keys()) {
  const s = SHAPE_STATE[st];
  if (s && (s.kind === 'stair' || s.kind === 'fence' || s.kind === 'wall' || s.kind === 'pane')) NEEDS_RESHAPE[st] = 1;
}
for (const id of [NETHER_BRICK_FENCE_ID, IRON_BARS_ID, ...NETHER_BRICK_STAIRS_IDS]) NEEDS_RESHAPE[id] = 1;
/** 1 for slab / stair ids: their cell holds no light of its own (they filter it), so the mesher lends them their neighbours'. */
export const BORROWS_LIGHT: Uint8Array = new Uint8Array(B.length);
for (const id of SHAPE_BORROW_LIGHT) BORROWS_LIGHT[id] = 1;

/**
 * Cross-drawn plants the mesher nudges off the cell centre (see `plantJitterX`): everything but the
 * torches and the crop planes. Their selection box moves with them.
 */
export const PLANT_JITTER: Uint8Array = new Uint8Array(B.length);
for (const b of B) {
  if (b && b.shape === RenderShape.Cross && !b.torch && IS_CROP_MODEL[b.id] === 0 && !b.collision) PLANT_JITTER[b.id] = 1;
}

/**
 * Vanilla's outline shapes for the cross-drawn plants, by display-name word (first match wins, so
 * 'Hanging Pointed Dripstone' and the '... Vines Plant' stalks come before the words inside them).
 * Anything unlisted (tall flowers, azalea, kelp, cobweb) keeps the whole cell.
 */
const PLANT_OUTLINE_BY_WORD: ReadonlyArray<readonly [CollisionRect, readonly string[]]> = [
  [[5, 0, 5, 11, 6, 11], ['Mushroom']],
  [[4, 0, 4, 12, 9, 12], ['Fungus']],
  [[5, 0, 5, 11, 10, 11], [
    'Poppy', 'Dandelion', 'Orchid', 'Allium', 'Bluet', 'Tulip', 'Daisy', 'Cornflower', 'Lily of the Valley',
  ]],
  [[2, 0, 2, 14, 13, 14], ['Short Grass', 'Fern', 'Dead Bush', 'Roots']],
  [[2, 0, 2, 14, 12, 14], ['Sapling', 'Seagrass']],
  [[5, 5, 5, 11, 16, 11], ['Hanging Pointed Dripstone']],
  [[5, 0, 5, 11, 11, 11], ['Pointed Dripstone']],
  [[2, 0, 2, 14, 16, 14], ['Sugar Cane']],
  [[3, 0, 3, 13, 16, 13], ['Bamboo']],
  [[1, 0, 1, 15, 16, 15], ['Cave Vines', 'Weeping Vines Plant', 'Sweet Berry Bush']],
  [[4, 0, 4, 12, 16, 12], ['Twisting Vines Plant']],
  [[4, 9, 4, 12, 16, 12], ['Weeping Vines']],
  [[4, 0, 4, 12, 15, 12], ['Twisting Vines']],
];

/** Height of the selection box of flat ground cover (petals, lily pads, carpets), in 1/16. */
const FLAT_TARGET_HEIGHT = 2;

/** The outline of a block that is smaller than its cell and not a shapes.ts state, or null. */
function smallOutline(b: BlockDef): readonly CollisionRect[] | null {
  if (b.collision) return b.collision; // chests, beds, doors, anvils, the brewing stand...
  if (b.shape === RenderShape.Flat) return [[0, 0, 0, 16, FLAT_TARGET_HEIGHT, 16]];
  if (b.shape === RenderShape.Rail) return [[0, 0, 0, 16, RAIL_SHAPE[b.id] >= 2 && RAIL_SHAPE[b.id] <= 5 ? 16 : FLAT_TARGET_HEIGHT, 16]];
  // the 6/16-thick pane models.ts draws
  if (b.id === NETHER_PORTAL_ID) return [[0, 0, 5, 16, 16, 11]];
  if (b.id === NETHER_PORTAL_Z_ID) return [[5, 0, 0, 11, 16, 16]];
  if (b.shape !== RenderShape.Cross) return null;
  if (b.torch) {
    // vanilla TorchBlock / WallTorchBlock: a 4x10x4 post, or a 5x10x5 box against its wall
    const face = TORCH_FACING[b.id];
    if (face < 0) return [[6, 0, 6, 10, 10, 10]];
    const [dx, , dz] = FACE_DIRS[face];
    const along = (d: number): [number, number] => (d > 0 ? [11, 16] : d < 0 ? [0, 5] : [5.5, 10.5]);
    const [x0, x1] = along(dx), [z0, z1] = along(dz);
    return [[x0, 3, z0, x1, 13, z1]];
  }
  // vanilla CropBlock / BeetrootBlock / NetherWartBlock: full width, growing with age
  const age = (ids: readonly number[]): number => ids.indexOf(b.id);
  if (b.id === WHEAT_ID) return [[0, 0, 0, 16, 16, 16]];
  if (age(WHEAT_STAGE_IDS) >= 0) return [[0, 0, 0, 16, (age(WHEAT_STAGE_IDS) + 1) * 2, 16]];
  if (age(CARROT_STAGE_IDS) >= 0) return [[0, 0, 0, 16, age(CARROT_STAGE_IDS) + 2, 16]];
  if (age(POTATO_STAGE_IDS) >= 0) return [[0, 0, 0, 16, age(POTATO_STAGE_IDS) + 2, 16]];
  if (age(BEETROOT_STAGE_IDS) >= 0) return [[0, 0, 0, 16, (age(BEETROOT_STAGE_IDS) + 1) * 2, 16]];
  if (age(NETHER_WART_IDS) >= 0) return [[0, 0, 0, 16, 5 + age(NETHER_WART_IDS) * 3, 16]];
  for (const [box, words] of PLANT_OUTLINE_BY_WORD) {
    if (words.some((w) => b.name.includes(w))) return [box];
  }
  return null;
}

/**
 * What a ray can hit inside a block's cell, and the outline drawn around it: the block's real shape
 * (clipped to the cell) in 1/16 units, or null for a whole-cell target. The block still owns its
 * whole cell; a ray that misses the shape carries on to whatever is behind it.
 */
export const TARGET_BOXES: (Float32Array | null)[] = new Array(B.length).fill(null);
const packBoxes = (boxes: readonly CollisionRect[]): Float32Array => {
  const f = new Float32Array(boxes.length * 6);
  boxes.forEach((b, k) => { for (let j = 0; j < 6; j++) f[k * 6 + j] = Math.min(16, b[j]); });
  return f;
};
for (const b of B) {
  if (!b) continue;
  const st = SHAPE_STATE[b.id];
  if (st) {
    if (!(st.kind === 'slab' && st.half === 2)) TARGET_BOXES[b.id] = packBoxes(stateVisual(st));
    continue;
  }
  const boxes = smallOutline(b);
  if (boxes && boxes.length > 0) TARGET_BOXES[b.id] = packBoxes(boxes);
}
for (const [id, boxes] of RS_OUTLINES) TARGET_BOXES[id] = packBoxes(boxes);
for (const [id, boxes] of U_OUTLINES) TARGET_BOXES[id] = packBoxes(boxes);
